import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import {
  FiArrowLeft,
  FiPlus,
  FiSearch,
  FiLayers,
  FiFileText,
  FiTrash2,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiZap,
  FiLayout,
  FiPercent,
  FiBarChart2,
  FiGrid,
  FiSettings,
  FiRefreshCcw,
  FiCheck,
  FiX,
  FiCloud,
  FiDownload,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { testService } from '../../../services/testService';
import toast from 'react-hot-toast';
import { useAssessmentContext } from '../AssessmentContext';
import '../assessment-module-mobile.css';
import PedagogyDropdownMenu from '../QuestionBank/PedagogyDropdownMenu';
import PedagogyFieldHeader from '../QuestionBank/PedagogyFieldHeader';
import { AssessmentEditIcon, assessTipProps } from '../AssessmentContext';

const ASSESSMENT_ACTION_ICON_BTN_CLASS =
  'w-9 h-9 rounded-xl flex items-center justify-center border transition-all shadow-sm shrink-0';

const AssessmentConfirmIconButton = ({
  onClick,
  title = 'Confirm',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-red-100 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white ${className}`}
  >
    <FiCheck className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentCancelIconButton = ({
  onClick,
  title = 'Cancel',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-slate-100 bg-slate-50 text-slate-500 hover:bg-slate-200 hover:text-slate-700 ${className}`}
  >
    <FiX className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentEditIconButton = ({
  onClick,
  title = 'Edit',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 ${className}`}
  >
    <AssessmentEditIcon className="w-4 h-4 shrink-0" />
  </button>
);

const AssessmentDeleteIconButton = ({
  onClick,
  title = 'Delete',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-rose-100 bg-rose-50 text-rose-500 hover:bg-[#b91c1c] hover:text-white ${className}`}
  >
    <FiTrash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
  </button>
);

const AssessmentAddIconButton = ({
  onClick,
  title = 'Add Question',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-emerald-100 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 ${className}`}
  >
    <FiPlus className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const MCQ_OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const isDescriptiveSectionType = (sectionType) => {
  const t = String(sectionType || '').toUpperCase();
  return t === 'DESCRIPTIVE' || t === 'DESC' || t === 'DESCRIPTIVE QUESTION' || t === 'SHORT ANSWER';
};

const getTemplateSectionLabel = (sectionType) =>
  isDescriptiveSectionType(sectionType) ? 'Descriptive' : 'MCQ';

const isMcqQuestionType = (type) => {
  const t = String(type ?? '').toUpperCase();
  return t === 'MCQ' || t === 'MULTIPLE CHOICE' || t === 'MULTIPLE-CHOICE';
};

const getMcqOptionText = (question, letter) => {
  const upper = String(letter ?? '').toUpperCase();
  const lower = upper.toLowerCase();
  if (!MCQ_OPTION_LETTERS.includes(upper)) return '';
  return String(question?.[`option_${lower}`] ?? question?.[`option_${upper}`] ?? '').trim();
};

const resolveMcqCorrectLetter = (question) => {
  const raw = String(question?.correct_answer ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  if (MCQ_OPTION_LETTERS.includes(upper)) return upper;

  const letterWithPunctuation = upper.match(/^([A-D])[\.\)\:\;\s-]*$/);
  if (letterWithPunctuation) return letterWithPunctuation[1];

  const rawLower = raw.toLowerCase();
  return (
    MCQ_OPTION_LETTERS.find((letter) => {
      const optionText = getMcqOptionText(question, letter).toLowerCase();
      return optionText !== '' && optionText === rawLower;
    }) || ''
  );
};

const isMcqCorrectOption = (question, letter) =>
  resolveMcqCorrectLetter(question) === String(letter ?? '').toUpperCase();

const normalizeMcqQuestionRow = (question) => {
  if (!isMcqQuestionType(question?.type)) return question;
  const resolved = resolveMcqCorrectLetter(question);
  return resolved ? { ...question, correct_answer: resolved } : question;
};

const normalizeQuestionForPaper = (question, section, sectionIndex) => {
  const normalizedType = String(question?.type || '').toUpperCase();
  const base = {
    ...question,
    question: (question?.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
    template_section: section.name,
    section_idx: sectionIndex,
    marks: Number(section.marks) || 1,
    template_marks: section.marks,
    type: (normalizedType === 'DESCRIPTIVE' || normalizedType === 'SHORT ANSWER' || normalizedType === 'SHORT_ANSWER' || normalizedType === '2-MARK') ? 'DESCRIPTIVE' : 'MCQ',
  };

  if (isMcqQuestionType(base.type)) {
    const resolved = resolveMcqCorrectLetter(base);
    if (resolved) base.correct_answer = resolved;
  }

  return base;
};

const QB_CSV_HEADERS = [
  'section_name',
  'question',
  'type',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'correct_answer',
  'marks',
  'expected_answer',
  'pedagogy',
];

const QB_CSV_INSTRUCTION =
  '# type: MCQ or Descriptive question. MCQ: fill options and correct_answer (A-D). Descriptive: leave options empty; put the model answer in correct_answer (and expected_answer if used).';

const DEFAULT_PEDAGOGY_OPTIONS = [
  'Algorithm Design',
  'Analytical Thinking',
  'Aptitude',
  'Code Efficiency',
  'Communication Skills',
  'Database Knowledge',
  'Data Interpretation',
  'Debugging',
  'Logical Reasoning',
];

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const normalizeQuestionBankHeaderKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const validateQuestionBankHeaderRow = (headerRow) => {
  const trimmed = headerRow.map((cell) => String(cell ?? '').trim());
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }

  if (trimmed.length !== QB_CSV_HEADERS.length) {
    return {
      valid: false,
      error: `Invalid column count (${trimmed.length}). Use the downloaded template with exactly these columns in order: ${QB_CSV_HEADERS.join(', ')}.`,
    };
  }

  const mismatches = [];
  trimmed.forEach((header, index) => {
    const expected = QB_CSV_HEADERS[index];
    if (normalizeQuestionBankHeaderKey(header) !== normalizeQuestionBankHeaderKey(expected)) {
      mismatches.push(`column ${index + 1} must be "${expected}" (found "${header}")`);
    }
  });

  if (mismatches.length > 0) {
    return {
      valid: false,
      error: `Invalid column names. ${mismatches.join('; ')}. Download the template and keep the header row unchanged.`,
    };
  }

  return { valid: true };
};

const parseQuestionBankCsvLine = (line) => {
  const result = [];
  let curValue = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(curValue.trim());
      curValue = '';
    } else {
      curValue += char;
    }
  }
  result.push(curValue.trim());
  return result;
};

const normalizeQuestionBankCsvType = (typeRaw) => {
  const type = String(typeRaw || '').trim().toLowerCase();
  if (
    type === 'short answer' ||
    type === 'descriptive' ||
    type === 'descriptive question' ||
    type.includes('descriptive')
  ) {
    return 'DESCRIPTIVE';
  }
  return 'MCQ';
};

const rowToQuestionBankItem = (cols, rowIndex) => {
  const cells = QB_CSV_HEADERS.map((_, index) => String(cols[index] ?? '').trim());
  const question = cells[1];
  if (!question) return null;

  const type = normalizeQuestionBankCsvType(cells[2]);
  const expectedAnswer = cells[9];
  const correctAnswer = cells[7] || (type === 'DESCRIPTIVE' ? expectedAnswer : '');

  return {
    id: Date.now() + rowIndex,
    section_name: cells[0] || 'General',
    question,
    type,
    option_a: cells[3],
    option_b: cells[4],
    option_c: cells[5],
    option_d: cells[6],
    correct_answer: correctAnswer,
    marks: cells[8] || (type === 'DESCRIPTIVE' ? '2' : '1'),
    pedagogy: cells[10] || 'General',
  };
};

const parseQuestionBankTableRows = (tableRows) => {
  const dataRows = (tableRows || [])
    .map((row) => (Array.isArray(row) ? row : Object.values(row)).map((cell) => String(cell ?? '').trim()))
    .filter((row) => {
      const first = String(row[0] ?? '').trim();
      return row.some((cell) => cell !== '') && !first.startsWith('#') && !first.toLowerCase().startsWith('type:');
    });

  if (dataRows.length === 0) {
    return { success: false, error: 'File is empty. Download the template and add your questions below the header row.' };
  }

  const headerCheck = validateQuestionBankHeaderRow(dataRows[0]);
  if (!headerCheck.valid) {
    return { success: false, error: headerCheck.error };
  }

  const questions = [];
  for (let i = 1; i < dataRows.length; i += 1) {
    const item = rowToQuestionBankItem(dataRows[i], i);
    if (item) questions.push(item);
  }

  if (questions.length === 0) {
    return {
      success: false,
      error: 'No valid question rows found. Each row needs a value in the "question" column.',
    };
  }

  return { success: true, questions };
};

const parseQuestionBankCsv = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const dataLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    dataLines.push(line);
  });

  const tableRows = dataLines.map((line) => parseQuestionBankCsvLine(line));
  return parseQuestionBankTableRows(tableRows);
};

const parseQuestionBankExcel = (arrayBuffer) => {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return { success: false, error: 'Excel file has no worksheets.' };
    }

    const sheet = workbook.Sheets[sheetName];
    const tableRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    return parseQuestionBankTableRows(tableRows);
  } catch (err) {
    console.error(err);
    return { success: false, error: 'Could not read the Excel file. Save as .csv from the template or upload a valid .xlsx file.' };
  }
};

const resolveTemplateBankName = (source) => {
  if (!source) return '';
  const raw = source.bank ?? source.question_bank ?? source.questionBank ?? '';
  if (typeof raw === 'string') return raw.trim();
  if (raw && typeof raw === 'object') return String(raw.name || '').trim();
  return '';
};

const TemplateBuilder = ({
  onBack,
  isWizardMode = false,
  isWizardStepActive = true,
  selectedTemplateId = null,
  onSelectTemplate = null,
  draftTemplate = null,
  wizardQuestionBank = null,
  onWizardTemplateBridge = null,
}) => {
  const { navigateToModule } = useAssessmentContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const filteredTemplates = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return savedTemplates;
    return savedTemplates.filter((t) => String(t.name || '').toLowerCase().includes(query));
  }, [savedTemplates, searchTerm]);

  const visibleBanks = useMemo(() => {
    if (!isWizardMode) return banks;

    const result = [];
    const addBank = (bankLike) => {
      const name = typeof bankLike === 'string' ? bankLike.trim() : String(bankLike?.name || '').trim();
      if (!name) return;
      const existing = banks.find((b) => String(b.name) === name);
      const id = existing?.id ?? bankLike?.id ?? `bank-${name}`;
      if (!result.some((b) => String(b.name) === name)) {
        result.push(existing || { id, name });
      }
    };

    if (wizardQuestionBank) {
      addBank(wizardQuestionBank);
    }
    if (selectedBank) {
      addBank(selectedBank);
    }

    return result.length > 0 ? result : banks;
  }, [banks, isWizardMode, wizardQuestionBank, selectedBank]);

  const fetchData = async () => {
    setSidebarLoading(true);
    try {
      const [banksRes, templatesRes] = await Promise.all([
        testService.getQuestionBanks(),
        testService.getTemplates()
      ]);

      if (banksRes.status === 'success') {
        setBanks(banksRes.banks);
      }
      if (templatesRes.status === 'success') {
        setSavedTemplates(templatesRes.templates);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSidebarLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [passMark, setPassMark] = useState('');
  const [isPassMarkFocused, setIsPassMarkFocused] = useState(false);
  const [isAddingSection, setIsAddingSection] = useState(true);
  const [showAddSectionTip, setShowAddSectionTip] = useState(false);
  const [templateSections, setTemplateSections] = useState([]);
  const [generatedPaper, setGeneratedPaper] = useState(null);
  const [activePreviewSection, setActivePreviewSection] = useState(0);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editingPaperQuestion, setEditingPaperQuestion] = useState(null); // { secIdx, qIdx }
  const [addingQuestionSecIdx, setAddingQuestionSecIdx] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [newSection, setNewSection] = useState({
    name: 'MCQ',
    type: 'MCQ',
    marks: 1,
    count: 10
  });

  const templateScrollRef = useRef(null);
  const templateNameInputRef = useRef(null);
  const previewSectionRef = useRef(null);
  const questionsPanelRef = useRef(null);
  const pendingScrollToPreviewRef = useRef(false);

  useEffect(() => {
    if (isWizardMode && !isWizardStepActive) return;
    if (!editingTemplateId && !isReadOnly && templateNameInputRef.current) {
      templateNameInputRef.current.focus();
    }
  }, [editingTemplateId, isReadOnly, isWizardMode, isWizardStepActive]);

  const defaultNewSection = {
    name: 'MCQ',
    type: 'MCQ',
    marks: 1,
    count: 10
  };

  const fileInputRef = useRef(null);

  // Inline Question Editor States
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [activeTab, setActiveTab] = useState('MCQ');
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState({ A: '', B: '', C: '', D: '' });
  const [correctOption, setCorrectOption] = useState(null);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [newMarks, setNewMarks] = useState(1);

  // Pedagogy States
  const [pedagogySearch, setPedagogySearch] = useState('');
  const [pedagogyOptions, setPedagogyOptions] = useState(DEFAULT_PEDAGOGY_OPTIONS);
  const [savingPedagogy, setSavingPedagogy] = useState(false);
  const [pedagogyDeleteMode, setPedagogyDeleteMode] = useState(false);
  const [selectedPedagogiesForDelete, setSelectedPedagogiesForDelete] = useState([]);
  const [deletingPedagogies, setDeletingPedagogies] = useState(false);
  const [showPedagogyDropdown, setShowPedagogyDropdown] = useState(false);
  const [pedagogyDropdownPos, setPedagogyDropdownPos] = useState(null);

  const pedagogyRef = useRef(null);
  const pedagogyAnchorRef = useRef(null);
  const pedagogyMenuRef = useRef(null);

  const lastCalledTemplate = useRef(null);
  const wizardDraftHydratedRef = useRef(false);
  const wizardDraftHydrationKeyRef = useRef('');
  const wizardTemplateLoadingIdRef = useRef(null);
  const wizardHydratedFromDraftRef = useRef(false);
  const wizardUserOwnsEditorRef = useRef(false);
  const wizardSyncSignatureRef = useRef('');
  const wizardSuppressSyncRef = useRef(false);
  const wizardActivationSeededRef = useRef(false);

  const buildGeneratedPaperFromDraft = useCallback((sections, questions = []) => {
    const normalized = (sections || []).map((sec) => {
      const rawType = String(sec.marks_type || sec.type || '').toUpperCase();
      const type = (rawType.includes('MULTIPLE') || rawType === 'MCQ') ? 'MCQ' : 'DESCRIPTIVE';
      return { ...sec, type };
    });

    return normalized.map((sec, sIdx) => {
      const secQuestions = (questions || []).filter((q) =>
        (q.template_section === sec.name) ||
        (q.section_idx !== undefined && Number(q.section_idx) === sIdx)
      );
      return {
        sectionName: sec.name,
        sectionMarks: sec.marks,
        sectionType: sec.type,
        questions: secQuestions.map((q) => normalizeQuestionForPaper(q, sec, sIdx)),
      };
    });
  }, []);

  const getWizardDraftHydrationKey = (template) => {
    if (!template) return '';
    return [
      template.id || '',
      template.name || '',
      template.passMark ?? '',
      template.bank || template.questionBank || '',
      JSON.stringify((template.sections || []).map((section) => ({
        name: section.name || section.section_name || '',
        type: section.type || section.marks_type || '',
        count: section.count || section.num_questions || 0,
        marks: section.marks || section.marks_per_question || 0,
      }))),
    ].join('|');
  };

  const markWizardEditorSynced = useCallback((template, sections, questionsArray = []) => {
    if (!isWizardMode || !template) return;

    const pm = template.pass_mark ?? template.passMark ?? '';
    const normalizedPassMark = pm === '' || pm === undefined || pm === null ? 0 : Number(pm) || 0;
    const bank = template.bank || template.questionBank || selectedBank || '';

    wizardDraftHydrationKeyRef.current = getWizardDraftHydrationKey({
      id: template.id,
      name: template.name,
      passMark: normalizedPassMark,
      bank,
      questionBank: bank,
      sections,
    });
    wizardDraftHydratedRef.current = true;
    lastCalledTemplate.current = {
      id: template.id ?? null,
      name: template.name || '',
      passMark: normalizedPassMark,
      bank,
      sections,
      questions: questionsArray,
    };
  }, [isWizardMode, selectedBank]);

  const getWizardSyncSignature = useCallback((payload) => {
    if (!payload) return '';
    const passMark = payload.passMark === '' || payload.passMark === undefined || payload.passMark === null
      ? 0
      : Number(payload.passMark) || 0;
    return [
      payload.id || '',
      payload.name || '',
      passMark,
      payload.bank || '',
      JSON.stringify(payload.sections || []),
      (payload.questions || []).map((q) => String(q.id ?? '')).join('|'),
    ].join('::');
  }, []);

  const syncWizardTemplateToParent = useCallback((payloadOverride = null) => {
    if (!isWizardMode || !onSelectTemplate) return;

    let payload = payloadOverride;
    if (!payload) {
      const questionsArray = generatedPaper ? generatedPaper.flatMap((s) => s.questions) : [];
      const normalizedPassMark = passMark === '' || passMark === undefined || passMark === null
        ? 0
        : Number(passMark) || 0;
      payload = {
        id: editingTemplateId,
        name: templateName,
        passMark: normalizedPassMark,
        bank: selectedBank,
        sections: templateSections,
        questions: questionsArray,
      };
    } else {
      payload = {
        ...payload,
        passMark: payload.passMark === '' || payload.passMark === undefined || payload.passMark === null
          ? 0
          : Number(payload.passMark) || 0,
      };
    }

    if (!payload.id && !String(payload.name || '').trim()) {
      if (wizardSyncSignatureRef.current !== '') {
        wizardSyncSignatureRef.current = '';
        onSelectTemplate(null);
      }
      return;
    }

    const signature = getWizardSyncSignature(payload);
    if (wizardSyncSignatureRef.current === signature) return;
    wizardSyncSignatureRef.current = signature;

    markWizardEditorSynced(payload, payload.sections || [], payload.questions || []);
    onSelectTemplate(payload);
  }, [
    isWizardMode,
    onSelectTemplate,
    generatedPaper,
    passMark,
    editingTemplateId,
    templateName,
    selectedBank,
    templateSections,
    getWizardSyncSignature,
    markWizardEditorSynced,
  ]);

  const buildWizardTemplateSnapshot = useCallback(() => {
    const questionsArray = generatedPaper ? generatedPaper.flatMap((s) => s.questions || []) : [];
    const normalizedPassMark = passMark === '' || passMark === undefined || passMark === null
      ? 0
      : Number(passMark) || 0;
    return {
      id: editingTemplateId,
      name: templateName,
      passMark: normalizedPassMark,
      bank: selectedBank,
      questionBank: selectedBank,
      sections: templateSections,
      questions: questionsArray,
    };
  }, [
    generatedPaper,
    passMark,
    editingTemplateId,
    templateName,
    selectedBank,
    templateSections,
  ]);

  useEffect(() => {
    if (!isWizardMode || !onWizardTemplateBridge) return undefined;
    onWizardTemplateBridge({
      flushSync: () => syncWizardTemplateToParent(),
      getSnapshot: () => buildWizardTemplateSnapshot(),
    });
    return () => {
      onWizardTemplateBridge(null);
    };
  }, [
    isWizardMode,
    onWizardTemplateBridge,
    syncWizardTemplateToParent,
    buildWizardTemplateSnapshot,
  ]);

  useEffect(() => {
    if (!isWizardMode || !wizardQuestionBank?.name || !wizardQuestionBank.questions?.length) return;
    setBanks((prev) => {
      const matchIdx = prev.findIndex((b) =>
        (wizardQuestionBank.id && String(b.id) === String(wizardQuestionBank.id)) ||
        String(b.name) === String(wizardQuestionBank.name)
      );
      if (matchIdx >= 0) {
        const next = [...prev];
        next[matchIdx] = { ...next[matchIdx], questions: wizardQuestionBank.questions };
        return next;
      }
      return [
        ...prev,
        {
          id: wizardQuestionBank.id,
          name: wizardQuestionBank.name,
          questions: wizardQuestionBank.questions,
        },
      ];
    });
  }, [isWizardMode, wizardQuestionBank?.id, wizardQuestionBank?.name, wizardQuestionBank?.questions?.length]);

  const loadBankQuestionsForGenerate = useCallback(async (bankName) => {
    if (!bankName) return null;

    const cached = banks.find((b) => String(b.name) === String(bankName));
    if (cached && Array.isArray(cached.questions) && cached.questions.length > 0) {
      return cached;
    }

    if (
      wizardQuestionBank?.name === bankName &&
      Array.isArray(wizardQuestionBank.questions) &&
      wizardQuestionBank.questions.length > 0
    ) {
      return {
        id: wizardQuestionBank.id ?? cached?.id,
        name: bankName,
        questions: wizardQuestionBank.questions,
      };
    }

    const bankId = cached?.id ?? wizardQuestionBank?.id;
    const numericId = bankId && /^\d+$/.test(String(bankId)) ? Number(bankId) : null;
    if (!numericId) return cached || null;

    try {
      const res = await testService.getQuestionBank(numericId);
      if (res?.status === 'success' && res.bank) {
        setBanks((prev) => {
          const idx = prev.findIndex((b) => String(b.id) === String(res.bank.id));
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...res.bank, questions: res.bank.questions || [] };
            return next;
          }
          return [...prev, { ...res.bank, questions: res.bank.questions || [] }];
        });
        return res.bank;
      }
    } catch (err) {
      console.error('Failed to load question bank for generate:', err);
    }

    return cached || null;
  }, [banks, wizardQuestionBank]);

  const applyDraftTemplateToEditor = useCallback((draft) => {
    if (!draft) return;

    wizardSuppressSyncRef.current = true;

    if (draft.id) {
      setEditingTemplateId(draft.id);
    } else if (!draft.name) {
      setEditingTemplateId(null);
    }

    if (draft.name) {
      setTemplateName(draft.name);
    }
    if (draft.passMark !== undefined) {
      const pm = draft.passMark;
      setPassMark(pm === 0 || pm === '0' || pm === undefined || pm === null ? '' : pm);
    }

    const bank = (isWizardMode && wizardQuestionBank?.name)
      ? wizardQuestionBank.name
      : resolveTemplateBankName(draft);
    if (bank) {
      setSelectedBank(bank);
    }

    if (draft.sections?.length) {
      const normalized = (draft.sections || []).map((sec) => {
        const rawType = String(sec.marks_type || sec.type || '').toUpperCase();
        const type = (rawType.includes('MULTIPLE') || rawType === 'MCQ') ? 'MCQ' : 'DESCRIPTIVE';
        return { ...sec, type };
      });
      setTemplateSections(normalized);
      setIsAddingSection(normalized.length === 0);
      setGeneratedPaper(buildGeneratedPaperFromDraft(normalized, draft.questions || []));
    }

    wizardDraftHydratedRef.current = true;
    wizardDraftHydrationKeyRef.current = getWizardDraftHydrationKey(draft);
    wizardHydratedFromDraftRef.current = true;

    window.setTimeout(() => {
      wizardSuppressSyncRef.current = false;
    }, 0);
  }, [buildGeneratedPaperFromDraft, isWizardMode, wizardQuestionBank?.name]);

  // Wizard: hydrate from draft only once (never re-apply parent draft after user picks a template)
  useEffect(() => {
    if (!isWizardMode || !draftTemplate || wizardHydratedFromDraftRef.current) return;
    if (wizardUserOwnsEditorRef.current) return;

    wizardHydratedFromDraftRef.current = true;

    // If draft has a saved template id but no sections (incomplete sync), fetch the full template
    const hasSections = Array.isArray(draftTemplate.sections) && draftTemplate.sections.length > 0;
    if (draftTemplate.id && !hasSections) {
      testService.getTemplate(draftTemplate.id)
        .then((res) => {
          if (res?.status === 'success' && res.template) {
            applyDraftTemplateToEditor({ ...draftTemplate, ...res.template });
          } else {
            applyDraftTemplateToEditor(draftTemplate);
          }
        })
        .catch(() => applyDraftTemplateToEditor(draftTemplate));
    } else {
      applyDraftTemplateToEditor(draftTemplate);
    }
  }, [isWizardMode, draftTemplate, applyDraftTemplateToEditor]);

  // Resolve wizard draft template id after sidebar list loads (without re-hydrating editor state)
  useEffect(() => {
    if (!isWizardMode || !draftTemplate?.name || draftTemplate.id || savedTemplates.length === 0) return;

    const matchingTemplate = savedTemplates.find((t) => t.name === draftTemplate.name);
    if (matchingTemplate) {
      setEditingTemplateId((prev) => (
        prev === matchingTemplate.id ? prev : matchingTemplate.id
      ));
    }
  }, [isWizardMode, draftTemplate?.name, draftTemplate?.id, savedTemplates]);

  useEffect(() => {
    if (isWizardMode && wizardQuestionBank?.name && selectedBank !== wizardQuestionBank.name) {
      setSelectedBank(wizardQuestionBank.name);
    }
  }, [isWizardMode, wizardQuestionBank, selectedBank]);

  // Wizard: seed parent draft once when the template step becomes active (after draft hydration)
  useEffect(() => {
    if (!isWizardMode) return;

    if (!isWizardStepActive) {
      wizardActivationSeededRef.current = false;
      return;
    }

    if (wizardActivationSeededRef.current || !wizardHydratedFromDraftRef.current) return;
    if (wizardUserOwnsEditorRef.current) return;

    wizardActivationSeededRef.current = true;
    syncWizardTemplateToParent();
  }, [isWizardMode, isWizardStepActive, syncWizardTemplateToParent]);

  // Wizard: debounced sync to parent on user edits only (skip programmatic loads)
  useEffect(() => {
    if (!isWizardMode || !isWizardStepActive) return undefined;
    if (wizardSuppressSyncRef.current) return undefined;

    const timer = window.setTimeout(() => {
      if (wizardSuppressSyncRef.current) return;
      syncWizardTemplateToParent();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    isWizardMode,
    isWizardStepActive,
    templateName,
    passMark,
    selectedBank,
    templateSections,
    generatedPaper,
    syncWizardTemplateToParent,
  ]);

  // Load Pedagogies
  useEffect(() => {
    let cancelled = false;
    const loadPedagogies = async () => {
      try {
        const res = await testService.getPedagogies();
        if (!cancelled && res?.status === 'success' && Array.isArray(res.pedagogies) && res.pedagogies.length > 0) {
          setPedagogyOptions(res.pedagogies);
        }
      } catch (err) {
        console.error('Failed to load pedagogies:', err);
      }
    };
    loadPedagogies();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedPedagogySearch = pedagogySearch.trim();
  const filteredPedagogyOptions = pedagogyOptions.filter((opt) =>
    opt.toLowerCase().includes(trimmedPedagogySearch.toLowerCase())
  );
  const canSaveNewPedagogy =
    trimmedPedagogySearch.length > 0 &&
    !pedagogyOptions.some((opt) => opt.toLowerCase() === trimmedPedagogySearch.toLowerCase());

  const syncPaperQuestionPedagogy = useCallback((secIdx, questionIdx, value) => {
    if (questionIdx === null || questionIdx === undefined || secIdx === null || secIdx === undefined) return;
    const pedagogy = (value || '').trim() || 'General';
    setGeneratedPaper((prev) => {
      if (!prev?.[secIdx]?.questions) return prev;
      const updated = [...prev];
      const section = { ...updated[secIdx] };
      section.questions = section.questions.map((item, idx) =>
        idx === questionIdx ? { ...item, pedagogy } : item
      );
      updated[secIdx] = section;
      return updated;
    });
  }, []);

  const handleSelectPedagogy = useCallback((value) => {
    setPedagogySearch(value);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    if (editingPaperQuestion !== null && !isAddingQuestion) {
      syncPaperQuestionPedagogy(editingPaperQuestion.secIdx, editingPaperQuestion.qIdx, value);
    }
  }, [editingPaperQuestion, isAddingQuestion, syncPaperQuestionPedagogy]);

  const handleSaveNewPedagogy = useCallback(async () => {
    if (!canSaveNewPedagogy || savingPedagogy) return;
    setSavingPedagogy(true);
    try {
      const res = await testService.savePedagogy(trimmedPedagogySearch);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        } else if (res.pedagogy) {
          setPedagogyOptions((prev) => {
            const next = [...prev, res.pedagogy];
            return next.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          });
        }
        setPedagogySearch(res.pedagogy || trimmedPedagogySearch);
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        if (editingPaperQuestion !== null && !isAddingQuestion) {
          syncPaperQuestionPedagogy(editingPaperQuestion.secIdx, editingPaperQuestion.qIdx, res.pedagogy || trimmedPedagogySearch);
        }
        toast.success(res.created === false ? 'Pedagogy already exists' : 'Pedagogy saved');
      } else {
        toast.error(res?.message || 'Failed to save pedagogy');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save pedagogy');
    } finally {
      setSavingPedagogy(false);
    }
  }, [canSaveNewPedagogy, savingPedagogy, trimmedPedagogySearch, editingPaperQuestion, isAddingQuestion, syncPaperQuestionPedagogy]);

  const syncPedagogyDropdownPosition = useCallback(() => {
    const anchor = pedagogyAnchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuMaxHeight = 240;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuMaxHeight + gap && rect.top > menuMaxHeight + gap;
    setPedagogyDropdownPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  }, []);

  const exitPedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode(false);
    setSelectedPedagogiesForDelete([]);
  }, []);

  const togglePedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPedagogiesForDelete([]);
        setShowPedagogyDropdown(true);
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setSelectedPedagogiesForDelete([]);
      }
      return next;
    });
  }, [syncPedagogyDropdownPosition]);

  const togglePedagogyDeleteSelect = useCallback((name) => {
    setSelectedPedagogiesForDelete((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }, []);

  const handleConfirmDeletePedagogies = useCallback(async () => {
    if (selectedPedagogiesForDelete.length === 0 || deletingPedagogies) return;
    setDeletingPedagogies(true);
    try {
      const res = await testService.deletePedagogies(selectedPedagogiesForDelete);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        }
        const deletedLower = new Set(
          (res.deleted || selectedPedagogiesForDelete).map((n) => String(n).toLowerCase())
        );
        if (deletedLower.has((pedagogySearch || '').toLowerCase())) {
          setPedagogySearch('');
        }
        exitPedagogyDeleteMode();
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        const count = res.deleted?.length ?? selectedPedagogiesForDelete.length;
        toast.success(count === 1 ? 'Pedagogy removed' : `${count} pedagogies removed`);
      } else {
        toast.error(res?.message || 'Failed to delete pedagogies');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete pedagogies');
    } finally {
      setDeletingPedagogies(false);
    }
  }, [
    selectedPedagogiesForDelete,
    deletingPedagogies,
    pedagogySearch,
    exitPedagogyDeleteMode,
  ]);

  const pedagogyDropdownMenuProps = {
    deleteMode: pedagogyDeleteMode,
    selectedForDelete: selectedPedagogiesForDelete,
    onToggleDeleteSelect: togglePedagogyDeleteSelect,
    onConfirmDelete: handleConfirmDeletePedagogies,
    deletingSelected: deletingPedagogies,
    onCancelDeleteMode: exitPedagogyDeleteMode,
    canSaveNew: pedagogyDeleteMode ? false : canSaveNewPedagogy,
    savingNew: savingPedagogy,
    onSelect: handleSelectPedagogy,
    onSaveNew: handleSaveNewPedagogy,
    onToggleDeleteMode: togglePedagogyDeleteMode,
  };

  const togglePedagogyDropdown = () => {
    setShowPedagogyDropdown((open) => {
      const next = !open;
      if (next) {
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setPedagogyDropdownPos(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!showPedagogyDropdown) return undefined;
    syncPedagogyDropdownPosition();
    const onScrollOrResize = () => syncPedagogyDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showPedagogyDropdown, syncPedagogyDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inField = pedagogyRef.current?.contains(event.target);
      const inMenu = pedagogyMenuRef.current?.contains(event.target);
      if (!inField && !inMenu) {
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        setPedagogyDeleteMode(false);
        setSelectedPedagogiesForDelete([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetModalState = () => { };

  const handleAddManualQuestion = (questionObj) => {
    const secType = questionObj.type;
    const secName = questionObj.section_name;
    const marks = Number(questionObj.marks);

    // 1. Update templateSections
    setTemplateSections((prevSections) => {
      const next = [...prevSections];
      const idx = next.findIndex(s => s.name === secName);
      if (idx > -1) {
        next[idx] = {
          ...next[idx],
          count: (Number(next[idx].count) || 0) + 1
        };
      } else {
        next.push({
          name: secName,
          type: secType,
          marks: marks,
          count: 1
        });
      }
      return next;
    });

    // 2. Update generatedPaper
    setGeneratedPaper((prevPaper) => {
      let paper = prevPaper ? JSON.parse(JSON.stringify(prevPaper)) : [];
      if (paper.length === 0) {
        if (templateSections.length > 0) {
          paper = templateSections.map(s => ({
            sectionName: s.name,
            sectionMarks: s.marks,
            sectionType: s.type,
            questions: []
          }));
        } else {
          paper = [{
            sectionName: secName,
            sectionMarks: marks,
            sectionType: secType,
            questions: []
          }];
        }
      }

      let sectionIdx = paper.findIndex(s => s.sectionName === secName);
      if (sectionIdx === -1) {
        sectionIdx = paper.length;
        paper.push({
          sectionName: secName,
          sectionMarks: marks,
          sectionType: secType,
          questions: []
        });
      }

      const newQ = {
        ...questionObj,
        id: Date.now(),
        template_section: secName,
        section_idx: sectionIdx,
        marks: marks,
        type: secType
      };

      paper[sectionIdx].questions.push(newQ);
      return paper;
    });

    toast.success("Question added successfully!");
  };

  const handleAddQuestion = (type) => {
    const targetType = type || 'MCQ';
    let matchedSecIdx = templateSections.findIndex(s => s.type === targetType);
    if (matchedSecIdx === -1) {
      matchedSecIdx = 0;
    }
    const matchedSec = templateSections[matchedSecIdx];
    const marks = matchedSec ? (Number(matchedSec.marks) || (targetType === 'MCQ' ? 1 : 2)) : (targetType === 'MCQ' ? 1 : 2);

    setAddingQuestionSecIdx(matchedSecIdx);
    setActiveTab(targetType);
    setNewMarks(marks);
    setEditingQuestionId(null);
    setIsAddingQuestion(true);
    setPedagogySearch('');
    setCorrectOption(null);
    setNewQuestion('');
    setNewOptions({ A: '', B: '', C: '', D: '' });
    setNewCorrectAnswer('');
  };

  const handleManualSave = () => {
    if (!newQuestion.trim()) return toast.error("Question text is required");
    if (activeTab === 'MCQ') {
      if (!newOptions.A || !newOptions.B || !newOptions.C || !newOptions.D) return toast.error("All options are required for MCQ");
      if (!correctOption) return toast.error("Please select a correct option");
    } else {
      if (!newCorrectAnswer.trim()) return toast.error("Expected answer is required for descriptive questions");
    }

    let secName = 'General';
    let secType = 'MCQ';
    let marks = 1;
    const targetSecIdx = addingQuestionSecIdx !== null ? addingQuestionSecIdx : activePreviewSection;
    if (generatedPaper && generatedPaper[targetSecIdx]) {
      secName = generatedPaper[targetSecIdx].sectionName;
      secType = generatedPaper[targetSecIdx].sectionType;
      marks = Number(generatedPaper[targetSecIdx].sectionMarks) || (secType === 'MCQ' ? 1 : 2);
    } else if (templateSections && templateSections[targetSecIdx]) {
      secName = templateSections[targetSecIdx].name;
      secType = templateSections[targetSecIdx].type;
      marks = Number(templateSections[targetSecIdx].marks) || (secType === 'MCQ' ? 1 : 2);
    }

    const questionObj = normalizeMcqQuestionRow({
      id: editingQuestionId || Date.now(),
      section_name: secName,
      question: newQuestion,
      type: secType,
      option_a: newOptions.A,
      option_b: newOptions.B,
      option_c: newOptions.C,
      option_d: newOptions.D,
      correct_answer: secType === 'MCQ' ? correctOption : newCorrectAnswer,
      expected_answer: secType === 'DESCRIPTIVE' ? newCorrectAnswer : '',
      marks: marks,
      pedagogy: pedagogySearch || 'General',
    });

    handleAddManualQuestion(questionObj);

    setIsAddingQuestion(false);
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  };

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    // 1. Instruction Row (Merged)
    const instructionStr = QB_CSV_INSTRUCTION.replace(/^# /, '');
    const firstRow = worksheet.getRow(1);
    firstRow.getCell(1).value = instructionStr;
    worksheet.mergeCells('A1:K1');

    const mergedCell = worksheet.getCell('A1');
    mergedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE599' } };
    mergedCell.font = { bold: true, color: { argb: 'FF1E293B' } };
    mergedCell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    firstRow.height = 30;

    // 2. Headers
    const headerRow = worksheet.getRow(2);
    QB_CSV_HEADERS.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true, color: { argb: 'FF1E293B' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // 3. Sample Rows
    const sampleRows = [
      ['General', 'What is 2+2?', 'MCQ', '3', '4', '5', '6', 'B', '1', '', 'Factual'],
      [
        'General',
        'Explain gravity.',
        'Descriptive question',
        '',
        '',
        '',
        '',
        'Force that pulls objects towards each other.',
        '2',
        'Force that pulls objects towards each other.',
        'Conceptual',
      ],
    ];

    sampleRows.forEach((rowValues, rowIndex) => {
      const row = worksheet.getRow(3 + rowIndex);
      rowValues.forEach((val, colIndex) => {
        row.getCell(colIndex + 1).value = val;
      });
    });

    // Adjust column widths
    worksheet.columns = [
      { width: 15 },
      { width: 35 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 10 },
      { width: 35 },
      { width: 20 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'question_template.xlsx');
  };

  const handleAddBulkQuestions = (uploadedQuestions) => {
    if (!uploadedQuestions || uploadedQuestions.length === 0) return;

    let currentSections = [...templateSections];
    let currentPaper = generatedPaper ? [...generatedPaper] : [];

    // If templateSections is empty, we populate sections and paper dynamically based on all uploaded questions (old behavior)
    if (currentSections.length === 0) {
      uploadedQuestions.forEach((q) => {
        const secType = String(q.type || '').toUpperCase() === 'DESCRIPTIVE' ? 'DESCRIPTIVE' : 'MCQ';
        const secName = q.section_name || getTemplateSectionLabel(secType);
        const marks = Number(q.marks) || (secType === 'MCQ' ? 1 : 2);

        // 1. Update currentSections
        const secIdx = currentSections.findIndex(s => s.name.toLowerCase() === secName.toLowerCase());
        if (secIdx > -1) {
          currentSections[secIdx] = {
            ...currentSections[secIdx],
            count: (Number(currentSections[secIdx].count) || 0) + 1
          };
        } else {
          currentSections.push({
            name: secName,
            type: secType,
            marks: marks,
            count: 1
          });
        }

        // 2. Update currentPaper
        let paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === secName.toLowerCase());
        const newQ = {
          ...q,
          question: (q.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
          template_section: secName,
          section_idx: paperSecIdx > -1 ? paperSecIdx : currentPaper.length,
          marks: marks,
          type: secType
        };

        if (paperSecIdx > -1) {
          currentPaper[paperSecIdx] = {
            ...currentPaper[paperSecIdx],
            questions: [...currentPaper[paperSecIdx].questions, newQ]
          };
        } else {
          paperSecIdx = currentPaper.length;
          newQ.section_idx = paperSecIdx;
          currentPaper.push({
            sectionName: secName,
            sectionMarks: marks,
            sectionType: secType,
            questions: [newQ]
          });
        }
      });

      setTemplateSections(currentSections);
      setGeneratedPaper(currentPaper);
      toast.success(`${uploadedQuestions.length} questions uploaded successfully!`);
      return;
    }

    // If templateSections is NOT empty, we fill the existing sections up to their defined count.
    // Initialize currentPaper with the existing sections if they are not already in currentPaper
    currentSections.forEach((sec, idx) => {
      const paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === sec.name.toLowerCase());
      if (paperSecIdx === -1) {
        currentPaper.push({
          sectionName: sec.name,
          sectionMarks: sec.marks,
          sectionType: sec.type,
          questions: []
        });
      }
    });

    let importedCount = 0;

    uploadedQuestions.forEach((q) => {
      const secType = String(q.type || '').toUpperCase() === 'DESCRIPTIVE' ? 'DESCRIPTIVE' : 'MCQ';
      const secName = q.section_name || '';

      // Find matching section in currentSections:
      // Match by name first, otherwise match by type.
      let matchedSecIdx = -1;
      if (secName) {
        matchedSecIdx = currentSections.findIndex(s => s.name.toLowerCase() === secName.toLowerCase());
      }
      if (matchedSecIdx === -1) {
        matchedSecIdx = currentSections.findIndex(s => s.type === secType);
      }

      if (matchedSecIdx > -1) {
        const matchedSec = currentSections[matchedSecIdx];
        const paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === matchedSec.name.toLowerCase());

        if (paperSecIdx > -1) {
          const currentQuestionsInSec = currentPaper[paperSecIdx].questions || [];
          const targetLimit = Number(matchedSec.count) || 0;

          if (currentQuestionsInSec.length < targetLimit) {
            const newQ = {
              ...q,
              question: (q.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
              template_section: matchedSec.name,
              section_idx: paperSecIdx,
              marks: Number(matchedSec.marks) || 1,
              type: matchedSec.type
            };
            currentPaper[paperSecIdx].questions = [...currentQuestionsInSec, newQ];
            importedCount++;
          }
        }
      }
    });

    setGeneratedPaper(currentPaper);
    if (importedCount > 0) {
      toast.success(`${importedCount} questions uploaded matching your template structure limits!`);
    } else {
      toast.error("No questions could be imported because the template structure limits were already reached or did not match.");
    }
  };

  const handleBulkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = isExcel
        ? parseQuestionBankExcel(e.target.result)
        : parseQuestionBankCsv(e.target.result);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      handleAddBulkQuestions(result.questions);
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }

    event.target.value = null;
  };

  const handleTemplateClick = () => {
    if (selectedBank) {
      if (templateSections.length === 0) {
        setIsAddingSection(true);
        toast.info("Please add at least one section to the template structure.");
      } else {
        handleGeneratePaper();
      }
    } else {
      setIsAddingSection(true);
      toast.info("Please add a template section or select a Question Bank first.");
    }
  };

  const resetToNewTemplate = () => {
    wizardDraftHydrationKeyRef.current = '';
    wizardDraftHydratedRef.current = false;
    wizardHydratedFromDraftRef.current = false;
    wizardUserOwnsEditorRef.current = false;
    wizardSyncSignatureRef.current = '';
    wizardSuppressSyncRef.current = false;
    wizardActivationSeededRef.current = false;
    setIsReadOnly(false);
    setEditingTemplateId(null);
    setTemplateName('');
    setSelectedBank((isWizardMode && wizardQuestionBank?.name) || '');
    setPassMark('');
    setTemplateSections([]);
    setGeneratedPaper(null);
    setActivePreviewSection(0);
    setEditingPaperQuestionIdx(null);
    setEditingSectionIdx(null);
    setIsAddingSection(true);
    setNewSection({ ...defaultNewSection });
    resetModalState();
    templateScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasTemplateDraft =
    Boolean(editingTemplateId) ||
    Boolean(templateName.trim()) ||
    Boolean(selectedBank) ||
    templateSections.length > 0 ||
    Boolean(generatedPaper);

  const totalQuestions = templateSections.reduce((acc, s) => acc + (Number(s.count) || 0), 0);
  const totalMarks = templateSections.reduce((acc, s) => acc + ((Number(s.count) || 0) * (Number(s.marks) || 0)), 0);

  const activeSectionsList = useMemo(() => {
    if (generatedPaper && generatedPaper.length > 0) {
      return generatedPaper;
    }
    return templateSections.map((sec) => ({
      sectionName: sec.name,
      sectionMarks: sec.marks,
      sectionType: sec.type,
      questions: [],
    }));
  }, [generatedPaper, templateSections]);

  const processedQuestions = useMemo(() => {
    const mcqs = [];
    const descs = [];
    
    activeSectionsList.forEach((sec, secIdx) => {
      (sec.questions || []).forEach((q, qIdx) => {
        const mapped = {
          ...q,
          _secIdx: secIdx,
          _qIdx: qIdx,
        };
        if (isMcqQuestionType(q.type)) {
          mcqs.push(mapped);
        } else {
          descs.push(mapped);
        }
      });
    });
    
    // Assign display index:
    const mcqsWithIndex = mcqs.map((q, idx) => ({
      ...q,
      _displayIndex: idx + 1,
    }));
    
    const descsWithIndex = descs.map((q, idx) => ({
      ...q,
      _displayIndex: mcqsWithIndex.length + idx + 1,
    }));
    
    return {
      mcqQuestions: mcqsWithIndex,
      descQuestions: descsWithIndex,
    };
  }, [activeSectionsList]);

  const hasPreviewQuestions = useMemo(
    () => activeSectionsList.some((section) => (section.questions?.length || 0) > 0),
    [activeSectionsList]
  );

  const scrollToQuestionPreview = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollRoot = templateScrollRef.current;
        const preview = previewSectionRef.current;
        if (scrollRoot && preview) {
          const top = Math.max(0, preview.offsetTop - 12);
          scrollRoot.scrollTo({ top, behavior: 'smooth' });
        } else {
          preview?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        questionsPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }, []);

  useEffect(() => {
    if (!pendingScrollToPreviewRef.current || !hasPreviewQuestions) return;
    pendingScrollToPreviewRef.current = false;
    scrollToQuestionPreview();
  }, [hasPreviewQuestions, generatedPaper, scrollToQuestionPreview]);

  const actualQuestionsCount = useMemo(() => {
    if (!generatedPaper) return 0;
    return generatedPaper.reduce((acc, sec) => acc + (sec.questions?.length || 0), 0);
  }, [generatedPaper]);

  const handleGeneratePaper = async () => {
    if (!selectedBank) return toast.error("Please select a Question Bank first.");
    if (templateSections.length === 0) return toast.error("Please add at least one section to the structure.");

    setLoading(true);
    try {
      const bank = await loadBankQuestionsForGenerate(selectedBank);
      if (!bank || !Array.isArray(bank.questions) || bank.questions.length === 0) {
        return toast.error("Selected bank has no questions.");
      }

      // Only use Active questions for generation
      const bankPool = (bank.questions || []).filter(q => (q.status || 'Active') === 'Active');

      let paper = [];
      templateSections.forEach((section, idx) => {
        const pool = bankPool.filter(q => (q.type || '').toUpperCase() === (section.type || '').toUpperCase());
        if (pool.length < section.count) {
          toast.error(`Not enough ${section.type} questions in the bank "${selectedBank}". Need ${section.count}, but only found ${pool.length}.`);
          return;
        }

        const shuffled = [...pool].sort(() => 0.5 - Math.random());
        const selected = shuffled
          .slice(0, section.count)
          .map((q) => normalizeQuestionForPaper(q, section, idx));

        paper.push({
          sectionName: section.name,
          sectionMarks: section.marks,
          sectionType: section.type,
          questions: selected
        });
      });

      if (paper.length !== templateSections.length) return;

      setGeneratedPaper(paper);
      setActivePreviewSection(0);

      if (isWizardMode) {
        syncWizardTemplateToParent({
          id: editingTemplateId,
          name: templateName,
          passMark: passMark === '' || passMark === undefined || passMark === null ? 0 : Number(passMark) || 0,
          bank: selectedBank,
          sections: templateSections,
          questions: paper.flatMap((section) => section.questions),
        });
      }

      pendingScrollToPreviewRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const handleReshuffle = () => {
    if (!generatedPaper) return;
    const reshuffled = generatedPaper.map(section => ({
      ...section,
      questions: [...section.questions].sort(() => 0.5 - Math.random())
    }));
    setGeneratedPaper(reshuffled);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return toast.error("Please enter a Template Name.");

    const isDuplicate = savedTemplates.some(t =>
      t.name.trim().toLowerCase() === templateName.trim().toLowerCase() &&
      String(t.id) !== String(editingTemplateId)
    );
    if (isDuplicate) {
      return toast.error("A template with this name already exists. Please choose a unique name.");
    }

    if (templateSections.length === 0) return toast.error("Please add at least one section.");

    setLoading(true);
    try {
      const isDbId = (val) => {
        if (!val) return false;
        const num = Number(val);
        return !isNaN(num) && Number.isInteger(num) && num > 0 && num < 10000000000;
      };

      const payload = {
        id: isDbId(editingTemplateId) ? Number(editingTemplateId) : null,
        name: templateName,
        passMark: passMark === '' ? 0 : Math.round(Number(passMark)),
        totalQuestions: Math.round(Number(totalQuestions)),
        totalMarks: Math.round(Number(totalMarks)),
        bank: selectedBank,
        sections: templateSections.map(s => ({
          name: s.name || s.section_name || 'Section',
          type: s.type || s.marks_type || 'MCQ',
          count: Math.round(Number(s.count || s.num_questions || 0)),
          marks: Math.round(Number(s.marks || s.marks_per_question || 1))
        })),
        questions: generatedPaper ? generatedPaper.flatMap(s => s.questions).map(q => ({
          ...q,
          marks: Math.round(Number(q.marks || 1))
        })) : []
      };
      const res = await testService.saveTemplate(payload);
      if (res.status === 'success') {
        toast.success(editingTemplateId ? "Template updated successfully!" : "Template saved successfully!");
        setEditingTemplateId(res.id);
        fetchData();
      } else {
        toast.error(res.message || "Failed to save template");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving template");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaperQuestion = (secIdx, qIdx, updatedQ) => {
    const updated = [...generatedPaper];
    updated[secIdx].questions[qIdx] = updatedQ;
    setGeneratedPaper(updated);
    setEditingPaperQuestionIdx(null);
  };

  const handleDeletePaperQuestion = (secIdx, qIdx) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete Question',
      message: 'Are you sure you want to remove this question from the template?',
      confirmText: 'Delete',
      onConfirm: () => {
        // 1. Remove the question from generatedPaper
        let updatedPaper = null;
        if (generatedPaper) {
          updatedPaper = generatedPaper.map((sec, sIdx) => {
            if (sIdx === secIdx) {
              return {
                ...sec,
                questions: sec.questions.filter((_, qI) => qI !== qIdx)
              };
            }
            return sec;
          });
          setGeneratedPaper(updatedPaper);
        }

        // 2. Decrement corresponding section count in templateSections
        setTemplateSections((prev) => {
          const next = [...prev];
          if (next[secIdx]) {
            const currentCount = Number(next[secIdx].count) || 0;
            next[secIdx] = {
              ...next[secIdx],
              count: Math.max(0, currentCount - 1)
            };
          }
          return next;
        });

        setPopupConfig({ isOpen: false });
        toast.success('Question deleted successfully');
      }
    });
  };

  const handleDeleteSection = (idx) => {
    const section = templateSections[idx];
    const sectionLabel =
      section?.name || getTemplateSectionLabel(section?.type);

    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this section?',
      message: `Are you sure you want to remove "${sectionLabel}" from the template structure?`,
      confirmText: 'Yes, delete it!',
      onConfirm: () => {
        setTemplateSections((prev) => {
          const next = prev.filter((_, i) => i !== idx);
          setIsAddingSection(next.length === 0);
          return next;
        });
        if (editingSectionIdx === idx) {
          setEditingSectionIdx(null);
        } else if (editingSectionIdx != null && editingSectionIdx > idx) {
          setEditingSectionIdx(editingSectionIdx - 1);
        }
        setGeneratedPaper(null);
        setPopupConfig({ isOpen: false });
        toast.success('Section deleted');
      },
    });
  };

  const templateNeedsFullLoad = (template) =>
    !Array.isArray(template?.sections) || template.sections.length === 0;

  const loadTemplateDetails = async (templateId) => {
    try {
      const res = await testService.getTemplate(templateId);
      if (res?.status === 'success' && res.template) {
        return res.template;
      }
    } catch (err) {
      console.error('Failed to load template:', err);
      toast.error('Failed to load template');
    }
    return null;
  };

  const applyTemplateToEditor = (template, readOnly = false) => {
    wizardSuppressSyncRef.current = true;

    setIsReadOnly(readOnly);
    setEditingTemplateId(template.id);
    setTemplateName(template.name);
    const bankName = (isWizardMode && wizardQuestionBank?.name)
      ? wizardQuestionBank.name
      : (resolveTemplateBankName(template) || '');
    setSelectedBank(bankName);
    const pm = template.pass_mark ?? template.passMark;
    setPassMark(pm === 0 || pm === undefined ? '' : pm);
    setActivePreviewSection(0);
    setEditingPaperQuestion(null);
    setIsAddingSection(false);

    const mappedSections = (template.sections || []).map(sec => {
      const rawType = String(sec.marks_type || sec.type || '').toUpperCase();
      const type = (rawType.includes('MULTIPLE') || rawType === 'MCQ') ? 'MCQ' : 'DESCRIPTIVE';
      return {
        name: sec.section_name || sec.name,
        type: type,
        count: sec.num_questions || sec.count,
        marks: sec.marks_per_question || sec.marks
      };
    });
    setTemplateSections(mappedSections);

    let questionsArray = [];
    if (template.questions && template.questions.length > 0) {
      const grouped = [];
      mappedSections.forEach((sec, sIdx) => {
        const secQuestions = (template.questions || []).filter(q =>
          (q.template_section === sec.name) ||
          (q.section_idx !== undefined && Number(q.section_idx) === sIdx)
        );
        grouped.push({
          sectionName: sec.name,
          sectionMarks: sec.marks,
          sectionType: sec.type,
          questions: secQuestions.map((q) => normalizeQuestionForPaper(q, sec, sIdx)),
        });
      });
      setGeneratedPaper(grouped);
      questionsArray = grouped.flatMap((section) => section.questions);
    } else if (mappedSections.length > 0) {
      setGeneratedPaper(mappedSections.map((sec) => ({
        sectionName: sec.name,
        sectionMarks: sec.marks,
        sectionType: sec.type,
        questions: [],
      })));
    } else {
      setGeneratedPaper(null);
    }

    const normalizedPassMark = pm === '' || pm === undefined || pm === null ? 0 : Number(pm) || 0;
    const payload = {
      id: template.id ?? null,
      name: template.name || '',
      passMark: normalizedPassMark,
      bank: bankName || selectedBank || '',
      sections: mappedSections,
      questions: questionsArray,
    };

    markWizardEditorSynced(template, mappedSections, questionsArray);

    window.setTimeout(() => {
      wizardSuppressSyncRef.current = false;
    }, 0);

    return payload;
  };

  const handleEditTemplate = async (template, readOnly = false) => {
    if (!template) return;

    if (
      String(template.id) === String(editingTemplateId) &&
      Array.isArray(generatedPaper) &&
      generatedPaper.length > 0
    ) {
      setIsReadOnly(readOnly);
      return;
    }

    if (wizardTemplateLoadingIdRef.current === String(template.id)) return;
    wizardTemplateLoadingIdRef.current = String(template.id);

    if (isWizardMode) {
      wizardUserOwnsEditorRef.current = true;
    }

    const needsFetch = templateNeedsFullLoad(template);
    if (needsFetch && !isWizardMode) {
      setContentLoading(true);
    }
    try {
      let full = template;
      if (needsFetch) {
        const loaded = await loadTemplateDetails(template.id);
        if (loaded) {
          full = loaded;
          setSavedTemplates((prev) =>
            prev.map((t) => (String(t.id) === String(full.id) ? { ...t, ...full } : t))
          );
        }
      }
      const payload = applyTemplateToEditor(full, readOnly);
      if (isWizardMode) {
        syncWizardTemplateToParent(payload);
      }
    } finally {
      wizardTemplateLoadingIdRef.current = null;
      if (needsFetch && !isWizardMode) {
        setContentLoading(false);
      }
    }
  };

  const handleCopyTemplate = async (template) => {
    setContentLoading(true);
    try {
      let full = template;
      if (templateNeedsFullLoad(template)) {
        const loaded = await loadTemplateDetails(template.id);
        if (loaded) full = loaded;
      }

      setIsReadOnly(false);
      setEditingTemplateId(null);
      setTemplateName(`${full.name} (Copy)`);
      setSelectedBank((isWizardMode && wizardQuestionBank?.name) ? wizardQuestionBank.name : (full.bank || ''));
      const pm2 = full.pass_mark ?? full.passMark;
      setPassMark(pm2 === 0 || pm2 === undefined ? '' : pm2);

      const mappedSections = (full.sections || []).map(sec => {
        const rawType = String(sec.marks_type || sec.type || '').toUpperCase();
        const type = (rawType.includes('MULTIPLE') || rawType === 'MCQ') ? 'MCQ' : 'DESCRIPTIVE';
        return {
          name: sec.section_name || sec.name,
          type: type,
          count: sec.num_questions || sec.count,
          marks: sec.marks_per_question || sec.marks
        };
      });
      setTemplateSections(mappedSections);
      setGeneratedPaper(null);
      setIsAddingSection(false);
    } finally {
      setContentLoading(false);
    }
  };

  const handleDeleteTemplate = (id) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this template?',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          const res = await testService.deleteTemplate(id);
          if (res.status === 'success') {
            setPopupConfig({ isOpen: false });
            if (String(editingTemplateId) === String(id)) {
              resetToNewTemplate();
            }
            fetchData();
            toast.success('Template deleted successfully');
          } else {
            const backendMsg = res.message;
            if (backendMsg && (backendMsg.includes('assigned to') || backendMsg.includes('test batch') || backendMsg.includes('linked'))) {
              toast.error('Unable to delete template. It is already linked to existing tests.');
            } else {
              toast.error(backendMsg || 'Failed to delete template');
            }
            setPopupConfig({ isOpen: false });
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          const backendMsg = err.response?.data?.message;
          if (backendMsg && (backendMsg.includes('assigned to') || backendMsg.includes('test batch') || backendMsg.includes('linked'))) {
            toast.error('Unable to delete template. It is already linked to existing tests.');
          } else {
            toast.error(backendMsg || 'Failed to delete template');
          }
        }
      }
    });
  };

  const lastNonZeroStats = useRef({ questions: 0, marks: 0 });
  const currentTotalQuestions = useMemo(() => {
    return activeSectionsList.reduce((acc, s) => acc + (s.questions?.length || 0), 0);
  }, [activeSectionsList]);

  const currentTotalMarks = useMemo(() => {
    return activeSectionsList.reduce((acc, s) => acc + ((s.questions?.length || 0) * (Number(s.sectionMarks) || 0)), 0);
  }, [activeSectionsList]);

  useEffect(() => {
    if (currentTotalQuestions > 0 || currentTotalMarks > 0) {
      lastNonZeroStats.current = { questions: currentTotalQuestions, marks: currentTotalMarks };
    }
  }, [currentTotalQuestions, currentTotalMarks]);

  const displayedTotalQuestions = (contentLoading && currentTotalQuestions === 0)
    ? lastNonZeroStats.current.questions
    : currentTotalQuestions;

  const displayedTotalMarks = (contentLoading && currentTotalMarks === 0)
    ? lastNonZeroStats.current.marks
    : currentTotalMarks;

  const memoizedStatsCards = useMemo(() => {
    return (
      <div className="assess-mod-paper-preview-stats flex items-center gap-2">
        <div className="assess-mod-paper-preview-stat bg-slate-50 rounded-lg border border-slate-100 flex items-center">
          <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">TOTAL QUESTIONS</span>
          <span className="text-[13px] font-black text-[#1e293b]">{displayedTotalQuestions}</span>
        </div>
        <div className="assess-mod-paper-preview-stat bg-rose-50 rounded-lg border border-rose-100 flex items-center">
          <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest text-[#b91c1c]">TOTAL MARKS</span>
          <span className="text-[13px] font-black text-[#b91c1c]">{displayedTotalMarks}</span>
        </div>
        {hasPreviewQuestions && (
          <button
            type="button"
            onClick={handleReshuffle}
            className="btn-assessment btn-assessment-danger btn-assessment-sm"
          >
            <FiRefreshCcw className="text-sm" />
            <span>Shuffle</span>
          </button>
        )}
      </div>
    );
  }, [displayedTotalQuestions, displayedTotalMarks, hasPreviewQuestions, handleReshuffle]);

  return (
    <div className={isWizardMode ? "flex flex-col flex-1 min-h-0 bg-[#f8fafc] overflow-hidden font-sans rounded-3xl border border-slate-100 shadow-xl" : "assess-mod-page assess-mod-template-page flex flex-col h-full min-h-0 bg-[#f8fafc] overflow-hidden font-sans"}>
      {/* Top Header */}
      {!isWizardMode && (
        <header className="assess-mod-header h-[70px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="assess-mod-back-mobile lg:hidden"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-lg" />
          </button>
          <div className="assess-mod-header-brand flex items-center gap-4">
            <div className="w-10 h-10 bg-[#b91c1c] rounded-xl flex items-center justify-center shadow-lg shadow-rose-200">
              <FiZap className="text-white text-xl fill-current" />
            </div>
            <div>
              <h1 className="text-[16px] font-black text-[#1e293b] tracking-tight leading-none">
                {isReadOnly
                  ? 'View Template'
                  : editingTemplateId
                    ? 'Edit Template'
                    : 'Create New Template'}
              </h1>
            </div>
          </div>
          <div className="assess-mod-header-actions flex items-center gap-3">
            {!isWizardMode && (
              <button
                type="button"
                onClick={onBack}
                className="btn-assessment btn-assessment-danger hidden lg:inline-flex"
              >
                <FiArrowLeft className="text-sm" /> Back
              </button>
            )}
          </div>
        </header>
      )}

      <div className="assess-mod-body flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="assess-mod-sidebar w-full lg:w-[300px] bg-[#f8fafc] border-e border-slate-200 flex flex-col shrink-0 overflow-hidden">
          <div className="assess-mod-sidebar-search bg-white px-6 pt-5 pb-4 border-b border-slate-200 shrink-0">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-[13px] font-black text-[#1e293b] uppercase tracking-widest mb-0 shrink min-w-0 leading-tight">
                Saved Templates
              </h2>
              <button
                type="button"
                onClick={resetToNewTemplate}
                className="assess-mod-sidebar-create-btn"
              >
                <FiPlus /> Create Template
              </button>
            </div>
          </div>
          <div className="assess-mod-sidebar-list flex-1 min-h-0 px-6 pt-4 pb-6 overflow-y-auto custom-scrollbar">
            <div className="relative mb-3 assess-mod-sidebar-search-field">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#b91c1c] text-xs pointer-events-none" />
              <input
                type="text"
                placeholder="Search templates..."
                className="w-full h-8 bg-white border border-red-200 rounded-lg pl-8 pr-2.5 text-[11px] font-semibold text-slate-700 shadow-sm ring-1 ring-red-50 outline-none transition-all placeholder:text-slate-400 focus:border-[#b91c1c] focus:ring-2 focus:ring-red-100"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {sidebarLoading ? (
              <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest py-6 px-2">
                Loading templates...
              </p>
            ) : filteredTemplates.length > 0 && (
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 px-0.5">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
              </p>
            )}
            <div className="space-y-2">
              {!sidebarLoading && filteredTemplates.map(t => (
                <div
                  key={t.id}
                  onClick={() => handleEditTemplate(t, true)}
                  className={`assess-mod-template-item w-full p-3 rounded-xl border transition-all cursor-pointer group flex items-center gap-3 relative shadow-sm ${String(editingTemplateId) === String(t.id) ? 'bg-rose-50 border-rose-200 ring-1 ring-rose-100' : 'bg-white border-slate-200 hover:border-rose-200 hover:shadow-md'}`}
                >
                  <div className="assess-mod-sidebar-item-icon">
                    <FiFileText />
                  </div>
                  <div className="flex-1 min-w-0 pr-2 group-hover:pr-[7.5rem] transition-all duration-300">
                    <h5 className={`text-[13px] font-black mb-0 tracking-tight truncate ${String(editingTemplateId) === String(t.id) ? 'text-[#b91c1c]' : 'text-[#1e293b]'}`}>
                      {t.name}
                    </h5>
                  </div>
                  <div
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur-sm rounded-xl px-1.5 py-1 border border-slate-100 shadow-md z-10"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleEditTemplate(t, false)}
                      className="w-9 h-9 bg-white text-blue-600 rounded-lg flex items-center justify-center border border-blue-100 hover:bg-blue-50 transition-all shadow-sm"
                      title="Edit Template"
                    >
                      <AssessmentEditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(t.id)}
                      className="w-9 h-9 bg-white text-rose-600 rounded-lg flex items-center justify-center border border-rose-100 hover:bg-rose-50 transition-all shadow-sm"
                      title="Delete Template"
                    >
                      <FiTrash2 className="text-base" />
                    </button>
                  </div>
                </div>
              ))}
              {savedTemplates.length === 0 && !sidebarLoading && (
                <div className="py-10 text-center">
                  <p className="text-[9px] text-slate-300 font-black uppercase tracking-widest">No templates yet</p>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="assess-mod-main-wrap assess-mod-template-main flex-1 min-w-0 min-h-0 overflow-hidden bg-[#f8fafc] flex flex-col">
          <div
            ref={templateScrollRef}
            className={`assess-mod-card-scroll assess-mod-template-scroll flex-1 min-h-0 w-full ${isWizardMode ? 'pb-20' : ''} ${hasPreviewQuestions ? 'assess-mod-template-scroll--preview-active' : ''}`}
          >
            {contentLoading && !isWizardMode && (
              <div className="px-6 pt-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">Loading template...</p>
              </div>
            )}

            {/* Top Setup Card */}
            <div className={`assess-mod-template-setup-stack ${hasPreviewQuestions ? 'assess-mod-template-setup-stack--compact' : ''}`}>
            <div className="assess-mod-template-setup-card bg-white rounded-[24px] border border-slate-100 shadow-xl shadow-slate-200/40 p-6">
              <div className="assess-mod-setup-grid grid grid-cols-4 gap-6 items-start">

                {/* Template Name */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-rose-50 text-[#b91c1c] rounded-xl flex items-center justify-center border border-rose-100 shadow-sm">
                      <FiFileText className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">TEMPLATE NAME</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">DEFINE TEMPLATE NAME</p>
                    </div>
                  </div>
                  <input
                    ref={templateNameInputRef}
                    type="text"
                    placeholder="e.g. Technical Skills 2024"
                    className="w-full bg-[#f8fafc] border border-slate-100 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all placeholder:text-slate-300 shadow-inner disabled:opacity-50"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>

                {/* Select Question Bank */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm shrink-0">
                      <FiGrid className="text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0">SELECT QUESTION BANK</h6>
                        {!isWizardMode && (
                          <button
                            type="button"
                            onClick={() => navigateToModule('questionBank', 'templates')}
                            className="shrink-0 p-1.5 bg-[#b91c1c] text-white rounded-full flex items-center justify-center hover:bg-[#991b1b] transition-colors leading-none"
                            title="Add question bank"
                            aria-label="Add question bank"
                          >
                            <FiPlus className="text-[10px] stroke-[2.5]" />
                          </button>
                        )}
                      </div>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">CHOOSE REPOSITORY</p>
                    </div>
                  </div>
                  <div className="relative">
                    <select
                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all appearance-none shadow-inner disabled:opacity-50"
                      value={selectedBank}
                      onChange={(e) => {
                        setSelectedBank(e.target.value);
                        setGeneratedPaper(null);
                      }}
                      disabled={isReadOnly}
                      onFocus={async () => {
                        try {
                          const res = await testService.getQuestionBanks();
                          if (res.status === 'success') setBanks(res.banks);
                        } catch (err) {
                          console.error("Failed to refresh banks:", err);
                        }
                      }}
                    >
                      <option value="">-- Select a Question Bank --</option>
                      {visibleBanks.map(bank => (
                        <option key={bank.id} value={bank.name}>{bank.name}</option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Pass Mark */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm">
                      <FiCheck className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">PASS MARK</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">MINIMUM SCORE TO PASS</p>
                    </div>
                  </div>
                  <input
                    type="number"
                    placeholder={isPassMarkFocused ? "" : "0"}
                    className="w-full bg-[#f8fafc] border border-slate-100 rounded-2xl px-5 py-3 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all shadow-inner disabled:opacity-50"
                    value={passMark}
                    onChange={(e) => setPassMark(e.target.value)}
                    onFocus={() => {
                      setIsPassMarkFocused(true);
                      if (passMark === 0 || passMark === '0') {
                        setPassMark('');
                      }
                    }}
                    onBlur={() => setIsPassMarkFocused(false)}
                    disabled={isReadOnly}
                  />
                </div>

                {/* Totals */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-50 text-slate-600 rounded-xl flex items-center justify-center border border-slate-100 shadow-sm">
                      <FiBarChart2 className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">TOTALS</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">FROM STRUCTURE</p>
                    </div>
                  </div>
                  <div className="flex items-center bg-[#f8fafc] rounded-xl border border-slate-100 overflow-hidden shadow-inner h-[42px]">
                    <div className="flex-1 text-center border-e border-slate-200">
                      <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">QUESTIONS</span>
                      <span className="text-[16px] font-black text-[#1e293b] leading-none">{totalQuestions}</span>
                    </div>
                    <div className="flex-1 text-center">
                      <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">MARKS</span>
                      <span className="text-[16px] font-black text-[#b91c1c] leading-none">{totalMarks}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-[#f8fafc] rounded-[24px] border border-slate-100 overflow-hidden assess-mod-section-table-wrap">
                {/* Table Header */}
                <div className="assess-mod-section-table-head px-8 py-3 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center bg-white border-b border-slate-100">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">SECTION / TYPE</span>
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">QUESTIONS</span>
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">MARKS EACH</span>
                  <div className="flex items-center justify-end gap-3">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">ACTIONS</span>
                    {!isReadOnly && templateSections.length < 2 && (
                      <div className="relative inline-block">
                        <button
                          onClick={() => setIsAddingSection(true)}
                          onMouseEnter={() => setShowAddSectionTip(true)}
                          onMouseLeave={() => setShowAddSectionTip(false)}
                          onFocus={() => setShowAddSectionTip(true)}
                          onBlur={() => setShowAddSectionTip(false)}
                          className="w-8 h-8 bg-[#b91c1c] text-white rounded-full flex items-center justify-center hover:bg-[#991b1b] transition-all shadow-md shadow-red-100"
                          aria-label="Add New Section."
                        >
                          <FiPlus className="text-sm" />
                        </button>

                      </div>
                    )}
                  </div>
                </div>

                {/* Existing Sections */}
                {templateSections.map((section, idx) => (
                  <div key={idx} className="border-b border-slate-50 last:border-0">
                    {editingSectionIdx === idx ? (
                      <div className="assess-mod-section-table-row px-8 py-4 bg-rose-50/20 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center animate-in fade-in duration-300">
                        <div className="flex-1">
                          <select
                            className="w-full bg-white border border-rose-100 rounded-xl px-4 py-2 text-[12px] font-black text-[#1e293b] outline-none shadow-sm"
                            value={section.type}
                            onChange={(e) => {
                              const updated = [...templateSections];
                              updated[idx] = { ...section, type: e.target.value, name: getTemplateSectionLabel(e.target.value) };
                              setTemplateSections(updated);
                            }}
                          >
                            <option value="MCQ">MCQ</option>
                            <option value="DESCRIPTIVE">Descriptive</option>
                          </select>
                        </div>
                        <div className="w-[120px]">
                          <input
                            type="number"
                            className="w-full bg-white border border-rose-100 rounded-xl px-2 py-2 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm"
                            value={section.count}
                            onChange={(e) => {
                              const updated = [...templateSections];
                              updated[idx] = { ...section, count: e.target.value };
                              setTemplateSections(updated);
                            }}
                          />
                        </div>
                        <div className="w-[120px]">
                          <input
                            type="number"
                            className="w-full bg-white border border-rose-100 rounded-xl px-2 py-2 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm"
                            value={section.marks}
                            onChange={(e) => {
                              const updated = [...templateSections];
                              updated[idx] = { ...section, marks: e.target.value };
                              setTemplateSections(updated);
                            }}
                          />
                        </div>
                        <div className="w-[160px] flex items-center justify-end gap-2">
                          <AssessmentConfirmIconButton
                            onClick={() => setEditingSectionIdx(null)}
                            title="Update section"
                          />
                          <AssessmentCancelIconButton
                            onClick={() => setEditingSectionIdx(null)}
                            title="Cancel"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="assess-mod-section-table-row px-8 py-4 bg-white grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center hover:bg-slate-50/30 transition-all group">
                        <div className="flex-1">
                          <div className="text-[12px] font-black text-[#1e293b]">{getTemplateSectionLabel(section.type)}</div>
                          <div className="text-[8px] text-slate-400 font-black uppercase tracking-[0.1em] mt-0.5">
                            {section.type === 'MCQ' ? 'MULTIPLE CHOICE' : 'DESCRIPTIVE QUESTIONS'}
                          </div>
                        </div>
                        <div className="w-[120px] text-center text-[12px] font-black text-[#1e293b]">{section.count}</div>
                        <div className="w-[120px] text-center text-[12px] font-black text-[#1e293b]">{section.marks}</div>
                        <div className="w-[140px] flex items-center justify-end gap-3 transition-all">
                          {!isReadOnly && (
                            <>
                              <AssessmentEditIconButton
                                onClick={() => setEditingSectionIdx(idx)}
                                title="Edit section"
                              />
                              <AssessmentDeleteIconButton
                                onClick={() => handleDeleteSection(idx)}
                                title="Delete section"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Input Row */}
                {isAddingSection && editingSectionIdx === null && (
                  <div className="assess-mod-section-table-input p-4 bg-slate-50/50 m-2 rounded-[24px] border-2 border-dashed border-slate-200 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center animate-in slide-in-from-top-2 duration-300">
                    <div className="flex-1 px-4">
                      <div className="relative">
                        <select
                          className="w-full bg-white border border-slate-200 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] outline-none appearance-none shadow-sm"
                          value={newSection.type}
                          onChange={(e) => setNewSection({ ...newSection, type: e.target.value, name: getTemplateSectionLabel(e.target.value) })}
                        >
                          <option value="">Choose Type...</option>
                          {!templateSections.some(s => s.type === 'MCQ') && <option value="MCQ">MCQ</option>}
                          {!templateSections.some(s => s.type === 'DESCRIPTIVE') && <option value="DESCRIPTIVE">Descriptive</option>}
                        </select>
                        <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                    </div>
                    <div className="w-[120px] px-2">
                      <input
                        type="number"
                        placeholder="0"
                        className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2.5 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm"
                        value={newSection.count}
                        onChange={(e) => setNewSection({ ...newSection, count: e.target.value })}
                      />
                    </div>
                    <div className="w-[120px] px-2">
                      <input
                        type="number"
                        placeholder="0"
                        className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2.5 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm"
                        value={newSection.marks}
                        onChange={(e) => setNewSection({ ...newSection, marks: e.target.value })}
                      />
                    </div>
                    <div className="w-[180px] px-4 flex items-center justify-end gap-2">
                      <AssessmentConfirmIconButton
                        onClick={() => {
                          if (!newSection.type) return toast.error("Select section type");
                          if (templateSections.length >= 2) return toast.error("Maximum 2 sections allowed");
                          const finalSection = {
                            ...newSection,
                            name: getTemplateSectionLabel(newSection.type),
                          };
                          setTemplateSections([...templateSections, finalSection]);
                          setNewSection({ name: '', type: 'MCQ', marks: 1, count: 10 });
                          setIsAddingSection(false);
                          setEditingSectionIdx(null);
                          setGeneratedPaper(null);
                        }}
                        title="Add section"
                      />
                      <AssessmentCancelIconButton
                        onClick={() => setIsAddingSection(false)}
                        title="Cancel"
                      />
                    </div>
                  </div>
                )}

                {!isAddingSection && templateSections.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 opacity-10">
                    <FiLayout className="text-6xl mb-4" />
                    <p className="text-[12px] font-black uppercase tracking-widest">No Sections Added</p>
                  </div>
                )}
              </div>

              {/* Final Actions */}
              {templateSections.length > 0 && selectedBank && (
                <div className="assess-mod-actions-row mt-8 flex justify-end gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <button
                    type="button"
                    onClick={handleGeneratePaper}
                    className="btn-assessment btn-assessment-danger"
                  >
                    <FiSettings className="text-sm" />
                    Generate QB
                  </button>
                </div>
              )}
            </div>
            </div>

            {/* Paper Preview Section */}
            {activeSectionsList.length > 0 && (
              <div
                ref={previewSectionRef}
                className="assess-mod-paper-preview assess-mod-paper-preview--fill flex flex-col flex-1 min-h-0 w-full"
                style={{
                  background: 'transparent',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '0px',
                  boxShadow: 'none',
                  padding: '0px',
                  margin: '0px',
                  overflow: 'visible',
                }}
              >
                <div className="assess-mod-paper-preview-head shrink-0 relative z-20 flex items-center justify-between border-b border-slate-50 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90">
                  <div className="assess-mod-paper-preview-head-inner flex items-center">
                    <div className="flex flex-col pr-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-[15px] font-black text-[#1e293b] uppercase tracking-tight mb-0">Question Paper Preview</h4>
                      </div>
                      <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest leading-none mt-1">LIVE GENERATED DRAFT</p>
                    </div>
                  </div>
                  {memoizedStatsCards}
                </div>

                <div className="assess-mod-paper-preview-body flex-1 min-h-0 overflow-hidden flex flex-col gap-8">
                  <div
                    ref={questionsPanelRef}
                    className="assess-mod-qb-questions-panel--template-scroll min-w-0 w-full flex-1 min-h-0 flex flex-col mt-3"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '0',
                      boxShadow: 'none',
                      padding: 0,
                      margin: 0,
                      overflow: 'visible',
                    }}
                  >
                    {/* Single common empty state when there are NO questions at all */}
                    {processedQuestions.mcqQuestions.length === 0 &&
                     processedQuestions.descQuestions.length === 0 &&
                     !isAddingQuestion && (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white border border-dashed border-slate-200 rounded-[24px] m-4 shadow-sm">
                        <FiLayers className="text-5xl mb-3 opacity-30 text-[#b91c1c]" />
                        <p className="text-[12px] font-black uppercase tracking-widest text-slate-600 mb-1">No Questions Available</p>
                        <p className="text-[10px] text-slate-400 font-bold px-6 text-center leading-normal">
                          This template does not contain any questions. Please generate questions or import them to preview the paper.
                        </p>
                      </div>
                    )}

                    {/* MCQ Questions Section */}
                    {(processedQuestions.mcqQuestions.length > 0 || (isAddingQuestion && activeTab === 'MCQ')) && (
                      <div className="assess-mod-paper-preview-section flex-1 min-h-0 flex flex-col">
                        <div className="assess-mod-paper-preview-section-head flex items-center gap-3 mb-4">
                          <span className="assess-mod-paper-preview-section-badge bg-[#1e293b] text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg px-3 py-1">
                            MCQ Questions
                          </span>
                          <span className="text-[7.5px] font-black uppercase tracking-wider text-rose-500 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0">
                            Double-click to view details
                          </span>
                          <div className="flex-1 bg-slate-200" style={{ height: '1px' }}></div>
                        </div>

                      <div className="assess-mod-paper-preview-questions pb-2 min-w-0 w-full flex-1 min-h-0 flex flex-col">
                        {/* Inline MCQ Add Question Row */}
                        {isAddingQuestion && activeTab === 'MCQ' && (
                          <div className="assess-mod-add-question mb-6 shrink-0 w-full min-w-0 mt-3">
                            <div className="assess-mod-qb-form-card rounded-lg border border-slate-200 border-l-4 border-l-[#b91c1c] bg-white p-4 sm:p-6 shadow-lg shadow-red-500/5 animate-in fade-in slide-in-from-top-4 duration-300">
                              <div className="assess-mod-add-question-fields lg:overflow-x-auto lg:custom-scrollbar lg:pb-1">
                                <div className="assess-mod-add-question-grid flex flex-col gap-4 w-full min-w-0 lg:grid lg:gap-6 lg:items-start lg:grid-cols-[60px_180px_minmax(0,1fr)_340px] px-6">
                                  {/* Col 1: Index */}
                                  <div className="flex flex-col items-center max-lg:flex-row max-lg:items-center max-lg:gap-3 max-lg:w-full max-lg:pb-3 max-lg:border-b max-lg:border-slate-100">
                                    <div className="w-10 h-10 bg-[#1e293b] text-white rounded-full flex items-center justify-center font-black text-[14px] shadow-lg border-4 border-white shrink-0">
                                      {processedQuestions.mcqQuestions.length + 1}
                                    </div>
                                    <div className="max-lg:mt-0 mt-2 text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-widest bg-red-50 text-[#b91c1c] border-red-100">
                                      MCQ
                                    </div>
                                  </div>

                                  {/* Col 2: Pedagogy */}
                                  <div className="relative" ref={pedagogyRef}>
                                    <PedagogyFieldHeader
                                      deleteMode={pedagogyDeleteMode}
                                      onToggleDeleteMode={togglePedagogyDeleteMode}
                                    />
                                    <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                      <input
                                        type="text"
                                        placeholder="Search or type pedagogy..."
                                        className={`w-full bg-[#f8fafc] border rounded-lg px-3 pr-8 py-2 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 cursor-pointer ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-100'}`}
                                        value={pedagogySearch}
                                        onChange={(e) => {
                                          setPedagogySearch(e.target.value);
                                          if (!showPedagogyDropdown) {
                                            setShowPedagogyDropdown(true);
                                            requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                          }
                                        }}
                                        onFocus={() => {
                                          setShowPedagogyDropdown(true);
                                          requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                        }}
                                        onClick={togglePedagogyDropdown}
                                      />
                                      <FiChevronDown
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-[12px] transition-all pointer-events-none ${showPedagogyDropdown ? 'text-[#b91c1c]' : 'text-slate-400'}`}
                                      />
                                    </div>

                                    {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                      <PedagogyDropdownMenu
                                        menuRef={pedagogyMenuRef}
                                        position={pedagogyDropdownPos}
                                        filteredOptions={filteredPedagogyOptions}
                                        {...pedagogyDropdownMenuProps}
                                      />,
                                      document.body
                                    )}
                                  </div>

                                  {/* Col 3: Question Content */}
                                  <div className="flex flex-col h-full">
                                    <div className="mb-1.5">
                                      <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5 max-lg:visible lg:invisible">QUESTION CONTENT</h6>
                                    </div>
                                    <textarea
                                      placeholder="Question..."
                                      rows="2"
                                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none leading-relaxed min-h-[90px]"
                                      value={newQuestion}
                                      onChange={(e) => setNewQuestion(e.target.value)}
                                    />
                                  </div>

                                  {/* Col 4: Options */}
                                  <div className="w-full min-w-0">
                                    <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5 lg:hidden">OPTION DETAILS</h6>
                                    <div className="grid grid-cols-2 gap-2">
                                      {['A', 'B', 'C', 'D'].map(opt => (
                                        <div key={opt} className="relative">
                                          <input
                                            type="text"
                                            placeholder={`${opt} ...`}
                                            className={`w-full bg-white border rounded-lg pl-8 pr-8 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-100'}`}
                                            value={newOptions[opt]}
                                            onChange={(e) => setNewOptions({ ...newOptions, [opt]: e.target.value })}
                                          />
                                          <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-black transition-all ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                                          <div
                                            onClick={() => setCorrectOption(opt)}
                                            className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] rounded-full border-2 cursor-pointer transition-all flex items-center justify-center shadow-[0_0_0_1px_rgba(0,0,0,0.08)] bg-white ${correctOption === opt ? 'border-[#b91c1c]' : 'border-[#9CA3AF] hover:border-[#b91c1c]'}`}
                                          >
                                            {correctOption === opt && <div className="w-2 h-2 bg-[#b91c1c] rounded-full"></div>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-[9px] font-semibold text-[#b91c1c] mt-1.5">
                                      <span className="font-black text-[#b91c1c]">Note:</span> Please select the correct option by clicking the corresponding radio button.
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="assess-mod-add-question-actions flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-5 pt-4 border-t border-slate-100 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setIsAddingQuestion(false)}
                                  className="btn-assessment w-full sm:w-auto bg-white !text-slate-600 border border-slate-200 !shadow-none hover:!bg-slate-50 hover:!text-slate-800"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleManualSave}
                                  className="btn-assessment btn-assessment-danger w-full sm:w-auto"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        )}



                        {processedQuestions.mcqQuestions.length > 0 && (
                          <div className="flex flex-col w-full">
                            <div className={`assess-mod-qb-questions-table-head ${isReadOnly ? 'assess-mod-qb-table--readonly' : ''}`}>
                              <span>#</span>
                              <span>Pedagogy</span>
                              <span>Question Content</span>
                              <span>Option Details</span>
                              {!isReadOnly && (
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span>Actions</span>
                                </div>
                              )}
                            </div>
                            <div className="assess-mod-qb-questions-table-body custom-scrollbar">
                              {processedQuestions.mcqQuestions.map((q) => {
                                const isEditingRow = editingPaperQuestion && editingPaperQuestion.secIdx === q._secIdx && editingPaperQuestion.qIdx === q._qIdx;
                                if (isEditingRow) {
                                  return (
                                    <div key={q.id || q._qIdx} className="assess-mod-qb-questions-table-row sticky top-0 z-10 shadow-md !bg-[#fff5f5] border-l-4 border-l-[#b91c1c] transition-all">
                                      {/* # */}
                                      <div className="assess-mod-qb-table-col--index">
                                        <span className="text-[14px] font-bold text-[#1e293b]">{q._displayIndex}</span>
                                      </div>

                                      {/* Pedagogy */}
                                      <div className="assess-mod-qb-table-col--pedagogy relative" ref={pedagogyRef}>
                                        <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                          <input
                                            type="text"
                                            placeholder="Pedagogy..."
                                            className={`w-full bg-[#f8fafc] border rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-200'}`}
                                            value={pedagogySearch}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setPedagogySearch(value);
                                              syncPaperQuestionPedagogy(q._secIdx, q._qIdx, value);
                                              if (!showPedagogyDropdown) {
                                                setShowPedagogyDropdown(true);
                                                requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                              }
                                            }}
                                            onFocus={() => {
                                              setShowPedagogyDropdown(true);
                                              requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                            }}
                                            onClick={togglePedagogyDropdown}
                                          />
                                        </div>
                                        {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                          <PedagogyDropdownMenu
                                            menuRef={pedagogyMenuRef}
                                            position={pedagogyDropdownPos}
                                            filteredOptions={filteredPedagogyOptions}
                                            {...pedagogyDropdownMenuProps}
                                          />,
                                          document.body
                                        )}
                                      </div>

                                      {/* Question Content */}
                                      <div className="assess-mod-qb-table-col--content min-w-0">
                                        <textarea
                                          className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                                          value={q.question}
                                          onChange={(e) => {
                                            const updated = [...generatedPaper];
                                            updated[q._secIdx].questions[q._qIdx].question = e.target.value;
                                            setGeneratedPaper(updated);
                                          }}
                                        />
                                      </div>

                                      {/* Option Details */}
                                      <div className="assess-mod-qb-table-col--options min-w-0">
                                        <div className="grid grid-cols-2 gap-1.5">
                                          {['A', 'B', 'C', 'D'].map((letter) => {
                                            const optionKey = `option_${letter.toLowerCase()}`;
                                            const isCorrect = String(q.correct_answer || '').toUpperCase() === letter;
                                            return (
                                              <div key={letter} className="relative">
                                                <input
                                                  type="text"
                                                  placeholder={`${letter} ...`}
                                                  className={`w-full bg-white border rounded-lg pl-5 pr-5 py-1 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${isCorrect ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-200'}`}
                                                  value={q[optionKey] || ''}
                                                  onChange={(e) => {
                                                    const updated = [...generatedPaper];
                                                    updated[q._secIdx].questions[q._qIdx][optionKey] = e.target.value;
                                                    setGeneratedPaper(updated);
                                                  }}
                                                />
                                                <span className={`absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-black ${isCorrect ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{letter}</span>
                                                <div
                                                  onClick={() => {
                                                    const updated = [...generatedPaper];
                                                    updated[q._secIdx].questions[q._qIdx].correct_answer = letter;
                                                    setGeneratedPaper(updated);
                                                  }}
                                                  className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${isCorrect ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-300 bg-white hover:border-red-400'}`}
                                                >
                                                  {isCorrect && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <p className="text-[9px] font-semibold text-[#b91c1c] mt-1.5">
                                          <span className="font-black text-[#b91c1c]">Note:</span> Please select the correct option by clicking the corresponding radio button.
                                        </p>
                                      </div>

                                      {/* Actions */}
                                      <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
                                        <div className="assess-mod-qb-table-actions-btns">
                                          <AssessmentConfirmIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion(null);
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                              setPedagogySearch('');
                                            }}
                                            title="Save question"
                                          />
                                          <AssessmentCancelIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion(null);
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                              setPedagogySearch('');
                                            }}
                                            title="Cancel edit"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div
                                    key={q.id || q._qIdx}
                                    onDoubleClick={() => setViewingQuestion(q)}
                                    className={`assess-mod-qb-questions-table-row cursor-pointer select-none ${isReadOnly ? 'assess-mod-qb-table--readonly' : ''}`}
                                  >
                                    {/* # */}
                                    <div className="assess-mod-qb-table-col--index">
                                      <span className="text-[14px] font-bold text-[#1e293b]">{q._displayIndex}</span>
                                    </div>

                                    {/* Pedagogy */}
                                    <div className="assess-mod-qb-table-col--pedagogy">
                                      <span className="assess-mod-qb-pedagogy-name">{q.pedagogy || 'General'}</span>
                                    </div>

                                    {/* Question Content */}
                                    <div className="assess-mod-qb-table-col--content min-w-0">
                                      <p className="assess-mod-qb-table-question" title={String(q.question || '').replace(/\s*\(Q\d+\)$/i, '')}>
                                        {String(q.question || '').replace(/\s*\(Q\d+\)$/i, '')}
                                      </p>
                                      <div className="assess-mod-qb-table-meta">
                                        <span>{parseInt(q.marks, 10) || 1} {parseInt(q.marks, 10) === 1 ? 'Mark' : 'Marks'}</span>
                                      </div>
                                    </div>

                                    {/* Option Details */}
                                    <div className="assess-mod-qb-table-col--options min-w-0">
                                      <div className="assess-mod-qb-options-grid">
                                        {['A', 'B', 'C', 'D'].map((letter) => {
                                          const optionText = q[`option_${letter.toLowerCase()}`];
                                          if (!optionText) return null;
                                          const isCorrect = String(q.correct_answer || '').toUpperCase() === letter;
                                          return (
                                            <div
                                              key={letter}
                                              className={`assess-mod-qb-option-cell ${isCorrect ? 'assess-mod-qb-option-cell--correct' : ''}`}
                                              title={optionText}
                                            >
                                              <span className="assess-mod-qb-option-cell-label">{letter.toLowerCase()})</span>
                                              <span className="assess-mod-qb-option-cell-text">{optionText}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    {!isReadOnly && (
                                      <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
                                        <div className="assess-mod-qb-table-actions-btns">
                                          <AssessmentEditIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion({ secIdx: q._secIdx, qIdx: q._qIdx });
                                              setPedagogySearch(q.pedagogy || '');
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                            }}
                                            title="Edit question"
                                          />
                                          <AssessmentDeleteIconButton
                                            onClick={() => handleDeletePaperQuestion(q._secIdx, q._qIdx)}
                                            title="Delete question"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                    {/* Section Divider */}
                    {(processedQuestions.mcqQuestions.length > 0 || (isAddingQuestion && activeTab === 'MCQ')) &&
                     (processedQuestions.descQuestions.length > 0 || (isAddingQuestion && activeTab === 'DESCRIPTIVE')) && (
                      <div className="my-8 bg-slate-200 shrink-0" style={{ height: '1px' }}></div>
                    )}

                    {/* Descriptive Questions Section */}
                    {(processedQuestions.descQuestions.length > 0 || (isAddingQuestion && activeTab === 'DESCRIPTIVE')) && (
                      <div className="assess-mod-paper-preview-section flex-1 min-h-0 flex flex-col">
                        <div className="assess-mod-paper-preview-section-head flex items-center gap-3 mb-4">
                          <span className="assess-mod-paper-preview-section-badge bg-[#1e293b] text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg px-3 py-1">
                            Descriptive Questions
                          </span>
                          <div className="flex-1 bg-slate-200" style={{ height: '1px' }}></div>
                        </div>

                      <div className="assess-mod-paper-preview-questions pb-2 min-w-0 w-full flex-1 min-h-0 flex flex-col">
                        {/* Inline Descriptive Add Question Row */}
                        {isAddingQuestion && activeTab === 'DESCRIPTIVE' && (
                          <div className="assess-mod-add-question mb-6 shrink-0 w-full min-w-0 mt-3">
                            <div className="assess-mod-qb-form-card rounded-lg border border-slate-200 border-l-4 border-l-[#b91c1c] bg-white p-4 sm:p-6 shadow-lg shadow-red-500/5 animate-in fade-in slide-in-from-top-4 duration-300">
                              <div className="assess-mod-add-question-fields lg:overflow-x-auto lg:custom-scrollbar lg:pb-1">
                                <div className="assess-mod-add-question-grid flex flex-col gap-4 w-full min-w-0 lg:grid lg:gap-6 lg:items-start lg:grid-cols-[60px_180px_minmax(0,1fr)_340px] px-6">
                                  {/* Col 1: Index */}
                                  <div className="flex flex-col items-center max-lg:flex-row max-lg:items-center max-lg:gap-3 max-lg:w-full max-lg:pb-3 max-lg:border-b max-lg:border-slate-100">
                                    <div className="w-10 h-10 bg-[#1e293b] text-white rounded-full flex items-center justify-center font-black text-[14px] shadow-lg border-4 border-white shrink-0">
                                      {processedQuestions.mcqQuestions.length + processedQuestions.descQuestions.length + 1}
                                    </div>
                                    <div className="max-lg:mt-0 mt-2 text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-widest bg-red-50 text-[#b91c1c] border-red-100">
                                      Descriptive
                                    </div>
                                  </div>

                                  {/* Col 2: Pedagogy */}
                                  <div className="relative" ref={pedagogyRef}>
                                    <PedagogyFieldHeader
                                      deleteMode={pedagogyDeleteMode}
                                      onToggleDeleteMode={togglePedagogyDeleteMode}
                                    />
                                    <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                      <input
                                        type="text"
                                        placeholder="Search or type pedagogy..."
                                        className={`w-full bg-[#f8fafc] border rounded-lg px-3 pr-8 py-2 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 cursor-pointer ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-100'}`}
                                        value={pedagogySearch}
                                        onChange={(e) => {
                                          setPedagogySearch(e.target.value);
                                          if (!showPedagogyDropdown) {
                                            setShowPedagogyDropdown(true);
                                            requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                          }
                                        }}
                                        onFocus={() => {
                                          setShowPedagogyDropdown(true);
                                          requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                        }}
                                        onClick={togglePedagogyDropdown}
                                      />
                                      <FiChevronDown
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 text-[12px] transition-all pointer-events-none ${showPedagogyDropdown ? 'text-[#b91c1c]' : 'text-slate-400'}`}
                                      />
                                    </div>

                                    {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                      <PedagogyDropdownMenu
                                        menuRef={pedagogyMenuRef}
                                        position={pedagogyDropdownPos}
                                        filteredOptions={filteredPedagogyOptions}
                                        {...pedagogyDropdownMenuProps}
                                      />,
                                      document.body
                                    )}
                                  </div>

                                  {/* Col 3: Question Content */}
                                  <div className="flex flex-col h-full">
                                    <div className="mb-1.5">
                                      <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5 max-lg:visible lg:invisible">QUESTION CONTENT</h6>
                                    </div>
                                    <textarea
                                      placeholder="Question..."
                                      rows="2"
                                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none leading-relaxed min-h-[90px]"
                                      value={newQuestion}
                                      onChange={(e) => setNewQuestion(e.target.value)}
                                    />
                                  </div>

                                  {/* Col 4: Expected Answer */}
                                  <div className="w-full min-w-0">
                                    <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5 lg:hidden">EXPECTED ANSWER</h6>
                                    <textarea
                                      placeholder="Expected Answer..."
                                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none min-h-[90px] leading-relaxed"
                                      value={newCorrectAnswer}
                                      onChange={(e) => setNewCorrectAnswer(e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="assess-mod-add-question-actions flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-5 pt-4 border-t border-slate-100 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setIsAddingQuestion(false)}
                                  className="btn-assessment w-full sm:w-auto bg-white !text-slate-600 border border-slate-200 !shadow-none hover:!bg-slate-50 hover:!text-slate-800"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleManualSave}
                                  className="btn-assessment btn-assessment-danger w-full sm:w-auto"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        )}



                        {processedQuestions.descQuestions.length > 0 && (
                          <div className="flex flex-col w-full">
                            <div className={`assess-mod-qb-questions-table-head ${isReadOnly ? 'assess-mod-qb-table--readonly' : ''}`}>
                              <span>#</span>
                              <span>Pedagogy</span>
                              <span>Question Content</span>
                              <span>Expected Answer</span>
                              {!isReadOnly && (
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span>Actions</span>
                                </div>
                              )}
                            </div>
                            <div className="assess-mod-qb-questions-table-body custom-scrollbar">
                              {processedQuestions.descQuestions.map((q) => {
                                const isEditingRow = editingPaperQuestion && editingPaperQuestion.secIdx === q._secIdx && editingPaperQuestion.qIdx === q._qIdx;
                                if (isEditingRow) {
                                  return (
                                    <div key={q.id || q._qIdx} className="assess-mod-qb-questions-table-row sticky top-0 z-10 shadow-md !bg-[#fff5f5] border-l-4 border-l-[#b91c1c] transition-all">
                                      {/* # */}
                                      <div className="assess-mod-qb-table-col--index">
                                        <span className="text-[14px] font-bold text-[#1e293b]">{q._displayIndex}</span>
                                      </div>

                                      {/* Pedagogy */}
                                      <div className="assess-mod-qb-table-col--pedagogy relative" ref={pedagogyRef}>
                                        <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                          <input
                                            type="text"
                                            placeholder="Pedagogy..."
                                            className={`w-full bg-[#f8fafc] border rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-200'}`}
                                            value={pedagogySearch}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setPedagogySearch(value);
                                              syncPaperQuestionPedagogy(q._secIdx, q._qIdx, value);
                                              if (!showPedagogyDropdown) {
                                                setShowPedagogyDropdown(true);
                                                requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                              }
                                            }}
                                            onFocus={() => {
                                              setShowPedagogyDropdown(true);
                                              requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                            }}
                                            onClick={togglePedagogyDropdown}
                                          />
                                        </div>
                                        {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                          <PedagogyDropdownMenu
                                            menuRef={pedagogyMenuRef}
                                            position={pedagogyDropdownPos}
                                            filteredOptions={filteredPedagogyOptions}
                                            {...pedagogyDropdownMenuProps}
                                          />,
                                          document.body
                                        )}
                                      </div>

                                      {/* Question Content */}
                                      <div className="assess-mod-qb-table-col--content min-w-0">
                                        <textarea
                                          className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                                          value={q.question}
                                          onChange={(e) => {
                                            const updated = [...generatedPaper];
                                            updated[q._secIdx].questions[q._qIdx].question = e.target.value;
                                            setGeneratedPaper(updated);
                                          }}
                                        />
                                      </div>

                                      {/* Option Details */}
                                      <div className="assess-mod-qb-table-col--options min-w-0">
                                        <textarea
                                          placeholder="Expected Answer..."
                                          className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                                          value={q.correct_answer || ''}
                                          onChange={(e) => {
                                            const updated = [...generatedPaper];
                                            updated[q._secIdx].questions[q._qIdx].correct_answer = e.target.value;
                                            setGeneratedPaper(updated);
                                          }}
                                        />
                                      </div>

                                      {/* Actions */}
                                      <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
                                        <div className="assess-mod-qb-table-actions-btns">
                                          <AssessmentConfirmIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion(null);
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                              setPedagogySearch('');
                                            }}
                                            title="Save question"
                                          />
                                          <AssessmentCancelIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion(null);
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                              setPedagogySearch('');
                                            }}
                                            title="Cancel edit"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div
                                    key={q.id || q._qIdx}
                                    onDoubleClick={() => setViewingQuestion(q)}
                                    className={`assess-mod-qb-questions-table-row cursor-pointer select-none ${isReadOnly ? 'assess-mod-qb-table--readonly' : ''}`}
                                  >
                                    {/* # */}
                                    <div className="assess-mod-qb-table-col--index">
                                      <span className="text-[14px] font-bold text-[#1e293b]">{q._displayIndex}</span>
                                    </div>

                                    {/* Pedagogy */}
                                    <div className="assess-mod-qb-table-col--pedagogy">
                                      <span className="assess-mod-qb-pedagogy-name">{q.pedagogy || 'General'}</span>
                                    </div>

                                    {/* Question Content */}
                                    <div className="assess-mod-qb-table-col--content min-w-0">
                                      <p className="assess-mod-qb-table-question" title={String(q.question || '').replace(/\s*\(Q\d+\)$/i, '')}>
                                        {String(q.question || '').replace(/\s*\(Q\d+\)$/i, '')}
                                      </p>
                                      <div className="assess-mod-qb-table-meta">
                                        <span>{parseInt(q.marks, 10) || 1} {parseInt(q.marks, 10) === 1 ? 'Mark' : 'Marks'}</span>
                                      </div>
                                    </div>

                                    {/* Option Details */}
                                    <div className="assess-mod-qb-table-col--options min-w-0">
                                      <div className="assess-mod-qb-desc-answer">
                                        <span className="font-semibold text-slate-600">Expected: </span>
                                        {q.correct_answer || '—'}
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    {!isReadOnly && (
                                      <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
                                        <div className="assess-mod-qb-table-actions-btns">
                                          <AssessmentEditIconButton
                                            onClick={() => {
                                              setEditingPaperQuestion({ secIdx: q._secIdx, qIdx: q._qIdx });
                                              setPedagogySearch(q.pedagogy || '');
                                              setShowPedagogyDropdown(false);
                                              setPedagogyDropdownPos(null);
                                            }}
                                            title="Edit question"
                                          />
                                          <AssessmentDeleteIconButton
                                            onClick={() => handleDeletePaperQuestion(q._secIdx, q._qIdx)}
                                            title="Delete question"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!isReadOnly && !isWizardMode && (
            <div className="assess-mod-template-save-fixed flex items-center justify-end">
              <button
                type="button"
                onClick={handleSaveTemplate}
                className="btn-assessment btn-assessment-danger"
              >
                Save Template
              </button>
            </div>
          )}
        </main>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
      />

      <AssessmentPopup
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
      {viewingQuestion && (
        <AssessmentPopup
          isOpen={true}
          title="Question Details"
          message=""
          type="info"
          showCancel={false}
          confirmText="Close"
          containerClassName="!max-w-2xl w-full"
          showIcon={false}
          align="left"
          onConfirm={() => setViewingQuestion(null)}
          onCancel={() => setViewingQuestion(null)}
        >
          <div className="text-left mt-2 space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar w-full">
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.type}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.pedagogy || 'General'}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.marks} Marks
              </span>
            </div>

            <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Question</h4>
              <p className="text-sm font-bold text-[#1e293b] whitespace-pre-wrap leading-relaxed">
                {viewingQuestion.question.replace(/\s*\(Q\d+\)$/i, '')}
              </p>
            </div>

            {isMcqQuestionType(viewingQuestion.type) ? (
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Options</h4>
                <div className="grid grid-cols-1 gap-2">
                  {['A', 'B', 'C', 'D'].map((letter) => {
                    const isCorrect = isMcqCorrectOption(viewingQuestion, letter);
                    return (
                      <div
                        key={letter}
                        className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-xs font-bold leading-relaxed ${isCorrect
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'bg-slate-50 border-slate-100 text-slate-600'
                          }`}
                      >
                        <span
                          className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-black shrink-0 ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200'
                            }`}
                        >
                          {letter}
                        </span>
                        <span className="break-words text-left">{getMcqOptionText(viewingQuestion, letter)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              (viewingQuestion.correct_answer || viewingQuestion.expected_answer) && (
                <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Expected Answer</h4>
                  <p className="text-sm font-bold text-slate-600 whitespace-pre-wrap leading-relaxed text-left">
                    {viewingQuestion.correct_answer || viewingQuestion.expected_answer}
                  </p>
                </div>
              )
            )}
          </div>
        </AssessmentPopup>
      )}
    </div>
  );
};

const styles = `
  select {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-image: none !important;
  }
  select::-ms-expand {
    display: none !important;
  }
  
  /* Remove number input arrows */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
`;

if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.innerHTML = styles;
  document.head.appendChild(styleTag);
}

export default TemplateBuilder;
